import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadWordmarkForPdf, WORDMARK_RATIO } from '../../utils/pdfLogo';
import exportService from '../../services/exportService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import GaugeMeter from '../../components/ui/GaugeMeter';
import QRCodeGenerator from '../../components/session/QRCodeGenerator';
import MicroSimSessionSection from '../../components/microsim/MicroSimSessionSection';
import { trainerService, clientService, sessionService } from '../../services';
import api from '../../services/api';
import { emojiToRating, getFeedbackRating } from '../../utils/ratingUtils';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';
import { formatLocalDateTime, formatLocalTime, formatActualDateTime, formatActualTime } from '../../utils/dateUtils';
import {
  getTRSInterpretation,
  getTEIInterpretation,
  getPCSInterpretation,
  getLAIInterpretation,
  getCRIInterpretation,
  getMetricBandInfo,
  getARSInterpretation,
} from '../../utils/reportingConstants';

/**
 * Session Report Page - Group Report for L&D
 * Multi-Day Training Engagement, Challenges & Action-Plan Insights
 * Available to: Trainer, Client, Super Admin
 */

const SessionReportPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatTime: formatTimeWithTimezone, formatDateWithTimezone } = usePreferences();
  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [isGeneratingZip, setIsGeneratingZip] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [showAIReport, setShowAIReport] = useState(false);
  const [aiReportHistory, setAiReportHistory] = useState([]);
  const [aiHistoryLoading, setAiHistoryLoading] = useState(false);
  const [isAiPdfGenerating, setIsAiPdfGenerating] = useState(false);

  // AI-powered challenge themes (semantic grouping by Gemini). Falls back to
  // word-frequency on the backend if AI isn't configured. `aiThemesSource`
  // tells us which path served the data so we can show a small badge.
  const [aiThemes, setAiThemes] = useState(null);
  const [aiThemesSource, setAiThemesSource] = useState(null);

  // Determine context based on URL
  const isTrainerView = location.pathname.includes('/trainer/');
  const isClientView = location.pathname.includes('/client/');
  const isAdminView = location.pathname.includes('/admin/');

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch session - use sessionService for all contexts (it works for all roles)
        const sessionResponse = await sessionService.getSession(sessionId);

        if (sessionResponse.success) {
          setSession(sessionResponse.data);

          // Also fetch participants if available
          try {
            let participantsResponse;
            if (isTrainerView) {
              participantsResponse = await trainerService.getSessionParticipants(sessionId);
            } else {
              // Use sessionService for both client and admin views
              participantsResponse = await sessionService.getParticipants(sessionId);
            }
            if (participantsResponse.success) {
              setParticipants(participantsResponse.data || []);
            }
          } catch (pErr) {
            console.log('Could not fetch participants:', pErr);
          }
        } else {
          setError('Failed to load session data');
        }
      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err.message || 'Failed to load session');
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      fetchSessionData();
    }
  }, [sessionId, isTrainerView, isClientView, isAdminView]);

  // Fetch AI-powered challenge themes (Gemini semantic grouping). The backend
  // endpoint returns { themes, source, total } — `source` is 'ai' when Gemini
  // produced the themes, 'word-frequency' when it fell back. We prefer this
  // over the inline word-count below whenever it returns something usable.
  useEffect(() => {
    if (!sessionId) return;
    const fetchThemes = async () => {
      try {
        const res = await api.get(`/ai/analytics/challenge-themes/${sessionId}`);
        if (res?.success && Array.isArray(res.data?.themes) && res.data.themes.length > 0) {
          setAiThemes(res.data.themes);
          setAiThemesSource(res.data.source || 'unknown');
        }
      } catch (err) {
        // Silently fall through — the inline word-frequency below will be used.
        console.warn('[challenge-themes] AI endpoint failed, using inline fallback:', err?.message);
      }
    };
    fetchThemes();
  }, [sessionId]);

  // Fetch AI report history when AI tab is selected
  useEffect(() => {
    if (activeTab === 'ai-report' && aiReportHistory.length === 0 && !aiHistoryLoading) {
      const fetchHistory = async () => {
        setAiHistoryLoading(true);
        try {
          const res = await api.get(`/ai/reports/session/${sessionId}/history`);
          if (res.success) setAiReportHistory(res.data);
        } catch (err) {
          console.error('Error loading AI report history:', err);
        } finally {
          setAiHistoryLoading(false);
        }
      };
      fetchHistory();
    }
  }, [activeTab, sessionId]);

  // Loading state
  if (loading) {
    return (
      <DashboardLayout title="Session Report">
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3"></div>
            <p className="text-neutral-600">Loading report...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error || !session) {
    return (
      <DashboardLayout title="Session Report">
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-neutral-500 mb-4">{error || 'Session not found'}</p>
            <Button variant="secondary" onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Use real data from API
  const sessionParticipants = participants || [];

  // Calculate unique participants from actual responses (most accurate count)
  const uniqueRespondentIds = new Set();
  session.subSessions?.forEach(ss => {
    ss.activities?.forEach(activity => {
      (activity.responses || []).forEach(r => {
        // Track unique participants by ID, email, or name
        const respondentId = r.participant?._id || r.participant || r.participantEmail || r.email || r.participantName || r.name;
        if (respondentId) {
          uniqueRespondentIds.add(respondentId.toString());
        }
      });
    });
  });
  const uniqueRespondentsCount = uniqueRespondentIds.size;

  // Get participant counts for accurate metrics
  const embeddedCount = session.participants?.length || 0;
  const fetchedCount = sessionParticipants.length || 0;
  const analyticsCount = session.participantCount || session.currentParticipants || session.analytics?.totalParticipants || 0;
  const expectedCount = session.expectedParticipants || 0;

  // Actual participants who joined the session (for engagement calculation)
  const actualParticipantCount = Math.max(uniqueRespondentsCount, embeddedCount, fetchedCount, analyticsCount) || 0;

  // For attendance display: show actual vs expected (if expected is set)
  // totalParticipants = expected count for attendance comparison, or actual if no expected set
  const totalParticipants = expectedCount > 0 ? expectedCount : actualParticipantCount;

  // Active participants = people who actually joined/responded (for engagement)
  // Pre-session participation. "Attended" is deliberately "did any of it" — see
  // utils/participantProgress on the backend for why a strict all-or-nothing
  // reading would mark genuine participants absent.
  const showPreSessionCol = Boolean(session?.preSession?.enabled);
  const preSessionAttended = sessionParticipants.filter((p) => p.preSession?.attended).length;

  // Post-session participation is measured by reflections: action plans and the
  // manager code are created for everyone automatically, so they prove nothing.
  // Progress is shown against cycles that have actually opened, because a 30-day
  // reflection that has not unlocked yet is not a missed one.
  const showReflectionCol = Boolean(
    session?.postSession?.enabled
    && session?.postSession?.reflectionCycles?.enabled !== false
    && sessionParticipants.some((p) => (p.reflections?.total || 0) > 0),
  );
  const reflectionAttended = sessionParticipants.filter((p) => p.reflections?.attended).length;
  const reflectionsOpen = Math.max(0, ...sessionParticipants.map((p) => p.reflections?.open || 0));

  /** Download the pre/post participation table as CSV. */
  const exportPrePostCsv = () => {
    const header = ['#', 'Name', 'Email', 'Participant Code'];
    if (showPreSessionCol) header.push('Pre-Session', 'Self-Assessment', 'Materials Viewed', 'Challenges', 'Expectations');
    if (showReflectionCol) header.push('Reflections', 'Completed', 'Open Cycles');

    const rows = [
      [`${session?.name || 'Session'} — Pre & Post Session Participation`],
      [`Session code`, session?.code || '-'],
      [`Total participants`, sessionParticipants.length],
    ];
    if (showPreSessionCol) rows.push(['Pre-session attended', preSessionAttended]);
    if (showReflectionCol) rows.push(['Reflections attended', reflectionAttended], ['Reflection cycles open so far', reflectionsOpen]);
    rows.push([], header);

    sessionParticipants.forEach((p, i) => {
      const row = [i + 1, p.name || 'Anonymous', p.email || '-', p.participantCode || '-'];
      if (showPreSessionCol) {
        const pre = p.preSession || {};
        row.push(pre.attended ? 'Attended' : 'Not attended');
        row.push(pre.totalQuestions > 0 ? `${pre.answered || 0}/${pre.totalQuestions}` : '-');
        row.push(pre.totalMaterials > 0 ? `${pre.materialsViewed || 0}/${pre.totalMaterials}` : '-');
        row.push(pre.challengeCount || 0);
        row.push(pre.expectationCount || 0);
      }
      if (showReflectionCol) {
        const ref = p.reflections || {};
        row.push(ref.attended ? 'Attended' : 'Not attended');
        row.push(ref.completed || 0);
        row.push(ref.open || 0);
      }
      rows.push(row);
    });

    // Same escaping and BOM as generateExcel above, so Excel opens it cleanly.
    const csv = rows.map((row) => row.map((cell) => {
      const cellStr = String(cell === null || cell === undefined ? '' : cell);
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    }).join(',')).join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${session?.name || 'Session'}_PrePost_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Same table as the CSV, as a PDF to hand to a client or print.
   * Mirrors the Participants "List" download: jsPDF + autoTable, already imported.
   * The PDF carries the on-screen columns only — the CSV keeps the extra breakdown
   * columns, which are for working through, not for reading on a page.
   */
  const exportPrePostPdf = async () => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 14;
    const rightX = pageWidth - marginX;

    // Returns null if the asset cannot be fetched or encoded, so a logo
    // failure degrades to a text-only header rather than losing the export.
    const wordmark = await loadWordmarkForPdf();
    if (wordmark) {
      const logoH = 7;
      doc.addImage(wordmark, 'PNG', marginX, 10, logoH * WORDMARK_RATIO, logoH);
    } else {
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text('Aktivity', marginX, 16);
    }

    // Right-hand side of the band: just when it was produced. The document
    // titles itself below, so a second heading here would only repeat it.
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Generated: ${new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`, rightX, 16, { align: 'right' });

    // Hairline rule under the band, matching the HTML template.
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(marginX, 23, rightX, 23);

    doc.setFontSize(16);
    doc.setTextColor(37, 99, 235);
    doc.text('Pre & Post Session Participation', marginX, 33);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Session: ${session?.name || 'Untitled'}`, marginX, 41);
    doc.text(`Code: ${session?.code || 'N/A'}  |  Total participants: ${sessionParticipants.length}`, marginX, 47);

    const summary = [];
    if (showPreSessionCol) summary.push(`Pre-session attended: ${preSessionAttended}/${sessionParticipants.length}`);
    if (showReflectionCol) summary.push(`Reflections attended: ${reflectionAttended}/${sessionParticipants.length}`);
    if (showReflectionCol && reflectionsOpen > 0) summary.push(`Cycles open so far: ${reflectionsOpen}`);
    if (summary.length) doc.text(summary.join('  |  '), marginX, 53);

    const head = ['#', 'Name', 'Participant Code'];
    if (showPreSessionCol) head.push('Pre-Session');
    if (showReflectionCol) head.push('Reflections');

    const body = sessionParticipants.map((p, i) => {
      const row = [i + 1, p.name || 'Anonymous', p.participantCode || '-'];
      if (showPreSessionCol) {
        const pre = p.preSession || {};
        row.push(pre.attended
          ? (pre.totalQuestions > 0 ? `Attended (${pre.answered}/${pre.totalQuestions})` : 'Attended')
          : 'Not attended');
      }
      if (showReflectionCol) {
        const ref = p.reflections || {};
        row.push(ref.attended ? `Attended (${ref.completed}/${ref.open || ref.total})` : 'Not attended');
      }
      return row;
    });

    autoTable(doc, {
      startY: summary.length ? 59 : 53,
      head: [head],
      body,
      theme: 'striped',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235] },
      columnStyles: { 0: { cellWidth: 10 }, 2: { cellWidth: 28, halign: 'center' } },
    });

    doc.save(`${session?.name || 'session'}_PrePost_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Manager access codes are generated at session end, and only when post-session
  // tracking and manager reinforcement are both switched on. Hide the column
  // entirely for sessions that never had them rather than showing a column of "-".
  const showManagerCode = Boolean(
    session?.postSession?.enabled
    && session?.postSession?.managerReinforcement?.enabled !== false
    && sessionParticipants.some((p) => p.managerCode),
  );

  const activeParticipantsCount = sessionParticipants.filter(p => p.status === 'joined' || p.status === 'online').length;
  const activeParticipants = activeParticipantsCount > 0 ? activeParticipantsCount : actualParticipantCount;

  // For engagement calculation, use actual participants (people who joined)
  // This measures how engaged attendees were, not attendance rate
  const participantsForEngagement = actualParticipantCount;

  // Get organization and trainer info from session
  const organization = session.organization || {};
  const trainer = session.trainer || {};

  // Program info
  const trainerName = trainer?.name || (trainer?.firstName && trainer?.lastName ? `${trainer.firstName} ${trainer.lastName}` : null) || session.trainerName || 'Trainer';

  // Calculate duration and date range for exports
  const actualStartTime = session.startTime || session.actualStartTime;
  const actualEndTime = session.endTime || session.actualEndTime;
  const isMultiDaySession = actualStartTime && actualEndTime &&
    new Date(actualStartTime).toISOString().slice(0, 10) !== new Date(actualEndTime).toISOString().slice(0, 10);

  let programDuration = 'N/A';
  let programDateRange = 'Not scheduled';

  if (isMultiDaySession) {
    // Multi-day session: show duration in days
    const startDate = new Date(actualStartTime);
    const endDate = new Date(actualEndTime);
    const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const diffTime = endDateOnly - startDateOnly;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    programDuration = `${diffDays} day${diffDays > 1 ? 's' : ''}`;

    // Format date range
    const startDateStr = formatDateWithTimezone(actualStartTime, { day: 'numeric', month: 'short' });
    const endDateStr = formatDateWithTimezone(actualEndTime, { day: 'numeric', month: 'short', year: 'numeric' });
    const startTimeStr = formatTimeWithTimezone(actualStartTime);
    const endTimeStr = formatTimeWithTimezone(actualEndTime);
    programDateRange = `${startDateStr} - ${endDateStr} (${startTimeStr} - ${endTimeStr} daily)`;
  } else if (actualStartTime) {
    // Single day session: show duration in hours/minutes
    if (session.duration) {
      programDuration = session.duration >= 60
        ? `${Math.floor(session.duration / 60)}h ${session.duration % 60 > 0 ? `${session.duration % 60}m` : ''}`
        : `${session.duration} minutes`;
    } else if (actualEndTime) {
      const durationMins = Math.round((new Date(actualEndTime) - new Date(actualStartTime)) / 60000);
      programDuration = durationMins >= 60
        ? `${Math.floor(durationMins / 60)}h ${durationMins % 60 > 0 ? `${durationMins % 60}m` : ''}`
        : `${durationMins} minutes`;
    }

    // Format single day date
    const dateStr = formatDateWithTimezone(actualStartTime, { day: 'numeric', month: 'short', year: 'numeric' });
    if (actualEndTime) {
      programDateRange = `${dateStr} • ${formatTimeWithTimezone(actualStartTime)} - ${formatTimeWithTimezone(actualEndTime)}`;
    } else {
      programDateRange = `${dateStr} • ${formatTimeWithTimezone(actualStartTime)}`;
    }
  }

  const programInfo = {
    title: session.name,
    mode: session.sessionType === 'public' ? 'Virtual' : (session.sessionType || 'In-Person'),
    facilitator: trainerName,
    hostedBy: organization?.name || session.organizationName || 'Organization',
    organization: organization?.name || session.organizationName || 'Organization',
    totalParticipants,
    activeParticipants,
    duration: programDuration,
    dateRange: programDateRange,
  };

  // Calculate aggregated stats from real session data
  // Total created activities (all activities regardless of status)
  const totalCreatedActivities = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.length || 0), 0) || 0;
  // Only count launched or completed activities (not pending/draft activities)
  const launchedActivities = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a => a.status === 'launched' || a.status === 'completed' || a.status === 'active').length || 0), 0) || 0;
  const completedActivities = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a => a.status === 'completed').length || 0), 0) || 0;

  // Helper: get accurate response count for an activity
  // Uses backend-computed counts from Participant records when available
  const getResponseCount = (activity) => {
    const actId = (activity._id || activity.id)?.toString();
    if (session.activityResponseCounts && actId) {
      return session.activityResponseCounts[actId] || 0;
    }
    return activity.responses?.length || 0;
  };

  // Calculate engagement from responses (only for launched/completed activities)
  // Use participantsForEngagement (actual joined participants) for accurate engagement calculation
  let totalLaunchedActivities = 0;
  const launchedActivityIds = new Set();
  session.subSessions?.forEach(ss => {
    ss.activities?.forEach(activity => {
      // Only count launched/completed/active activities
      if (activity.status === 'launched' || activity.status === 'completed' || activity.status === 'active') {
        totalLaunchedActivities++;
        launchedActivityIds.add((activity._id || activity.id)?.toString());
      }
    });
  });

  // Use backend-computed total from Participant records (source of truth)
  // Only count responses for launched activities
  let totalResponses = 0;
  if (session.activityResponseCounts) {
    // Use accurate counts from Participant collection
    Object.entries(session.activityResponseCounts).forEach(([actId, count]) => {
      if (launchedActivityIds.has(actId)) {
        totalResponses += count;
      }
    });
  } else {
    // Fallback: count from Session's activity responses
    session.subSessions?.forEach(ss => {
      ss.activities?.forEach(activity => {
        if (activity.status === 'launched' || activity.status === 'completed' || activity.status === 'active') {
          totalResponses += activity.responses?.length || 0;
        }
      });
    });
  }

  // Calculate possible responses: launched activities * actual joined participants
  const totalPossibleResponses = totalLaunchedActivities * participantsForEngagement;

  const avgEngagement = totalPossibleResponses > 0
    ? Math.min(100, Math.round((totalResponses / totalPossibleResponses) * 100))
    : Math.min(100, session.analytics?.engagementRate || session.engagementRate || 0);

  const totalQuestionsAsked = session.analytics?.questionsAsked ||
    sessionParticipants.reduce((sum, p) => sum + (p.questionsAsked || 0), 0) || 0;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'activities', label: 'Activities' },
    { id: 'participants', label: 'Participants' },
    // Only meaningful for sessions that actually use the pre/post features.
    ...(showPreSessionCol || showReflectionCol ? [{ id: 'pre-post', label: 'Pre & Post' }] : []),
    { id: 'feedback', label: 'Feedback' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'ai-report', label: 'AI Impact' },
  ];

  // Calculate real activity response counts (using accurate Participant-based counts)
  const pollResponses = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a => a.type === 'poll').reduce((s, a) => s + getResponseCount(a), 0) || 0), 0) || 0;
  const workbookReflections = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a => a.type === 'feedback' || a.type === 'wordCloud').reduce((s, a) => s + getResponseCount(a), 0) || 0), 0) || 0;
  const challengeResponses = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a => a.type === 'challenges').reduce((s, a) => s + getResponseCount(a), 0) || 0), 0) || 0;
  const actionPlanResponses = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a => a.type === 'actionPlans' || a.type?.toLowerCase() === 'actionplans').reduce((s, a) => s + getResponseCount(a), 0) || 0), 0) || 0;

  // Calculate average rating - prioritize overall session feedback if available
  // Using shared emojiToRating utility from ratingUtils.js

  // First check for overall session feedback
  const overallFeedbackResponses = session.overallFeedback?.responses || [];
  const hasOverallFeedback = session.overallFeedback?.enabled && overallFeedbackResponses.length > 0;

  let ratingSum = 0;
  let ratingCount = 0;

  if (hasOverallFeedback) {
    // Use overall session feedback ratings
    overallFeedbackResponses.forEach(response => {
      if (response.rating && response.rating > 0 && response.rating <= 5) {
        ratingSum += response.rating;
        ratingCount++;
      }
    });
  } else {
    // Fall back to subsession feedback activities
    const feedbackActivities = session.subSessions?.flatMap(ss =>
      ss.activities?.filter(a => a.type === 'feedback') || []
    ) || [];

    feedbackActivities.forEach(activity => {
      (activity.responses || []).forEach(response => {
        const resp = response.response;
        let rating = null;

        // Check for numeric rating in various formats
        if (typeof resp?.rating === 'number') {
          rating = resp.rating;
        } else if (typeof resp?.score === 'number') {
          rating = resp.score;
        } else if (typeof resp?.value === 'number') {
          rating = resp.value;
        } else if (typeof response?.rating === 'number') {
          // Rating directly on response object
          rating = response.rating;
        }
        // Check for emoji rating
        else if (resp?.emoji || resp?.selectedEmoji) {
          rating = emojiToRating(resp.emoji || resp.selectedEmoji);
        } else if (response?.emoji) {
          // Emoji directly on response object
          rating = emojiToRating(response.emoji);
        }
        // Check if response itself is an emoji or number
        else if (typeof resp === 'string') {
          rating = emojiToRating(resp);
        } else if (typeof resp === 'number') {
          rating = resp;
        }

        // Use getFeedbackRating as fallback
        if (!rating && (resp?.emoji || response?.emoji)) {
          rating = getFeedbackRating(resp?.emoji || response?.emoji);
        }

        if (rating && rating > 0 && rating <= 5) {
          ratingSum += rating;
          ratingCount++;
        }
      });
    });
  }

  const calculatedAvgRating = ratingCount > 0 ? Math.round((ratingSum / ratingCount) * 10) / 10 : 0;

  // Calculate emoji distribution from feedback data (prioritize overall feedback)
  const emojiDistribution = {
    veryDisappointed: 0, // rating 1
    disappointed: 0,     // rating 2
    neutral: 0,          // rating 3
    satisfied: 0,        // rating 4
    excited: 0,          // rating 5
  };

  if (hasOverallFeedback) {
    // Use overall session feedback for emoji distribution
    overallFeedbackResponses.forEach(response => {
      const rating = response.rating;
      if (rating && rating > 0) {
        if (rating === 1) emojiDistribution.veryDisappointed++;
        else if (rating === 2) emojiDistribution.disappointed++;
        else if (rating === 3) emojiDistribution.neutral++;
        else if (rating === 4) emojiDistribution.satisfied++;
        else if (rating >= 5) emojiDistribution.excited++;
      }
    });
  } else {
    // Fall back to subsession feedback activities
    const feedbackActivities = session.subSessions?.flatMap(ss =>
      ss.activities?.filter(a => a.type === 'feedback') || []
    ) || [];

    feedbackActivities.forEach(activity => {
      (activity.responses || []).forEach(response => {
        const resp = response.response;
        let rating = null;

        // Get rating value - same logic as overall rating calculation
        if (typeof resp?.rating === 'number') {
          rating = resp.rating;
        } else if (typeof resp?.score === 'number') {
          rating = resp.score;
        } else if (typeof resp?.value === 'number') {
          rating = resp.value;
        } else if (typeof response?.rating === 'number') {
          rating = response.rating;
        } else if (resp?.emoji || resp?.selectedEmoji) {
          rating = emojiToRating(resp.emoji || resp.selectedEmoji);
        } else if (response?.emoji) {
          rating = emojiToRating(response.emoji);
        } else if (typeof resp === 'string') {
          rating = emojiToRating(resp);
        } else if (typeof resp === 'number') {
          rating = resp;
        }

        // Use getFeedbackRating as fallback
        if (!rating && (resp?.emoji || response?.emoji)) {
          rating = getFeedbackRating(resp?.emoji || response?.emoji);
        }

        // Categorize rating
        if (rating && rating > 0) {
          if (rating === 1) emojiDistribution.veryDisappointed++;
          else if (rating === 2) emojiDistribution.disappointed++;
          else if (rating === 3) emojiDistribution.neutral++;
          else if (rating === 4) emojiDistribution.satisfied++;
          else if (rating >= 5) emojiDistribution.excited++;
        }
      });
    });
  }

  const totalFeedbackResponses = emojiDistribution.veryDisappointed + emojiDistribution.disappointed + emojiDistribution.neutral + emojiDistribution.satisfied + emojiDistribution.excited;

  // Calculate 5-question feedback averages (new format)
  const feedbackQuestionAverages = {
    topicRelevance: { sum: 0, count: 0, avg: 0 },
    trainerEffectiveness: { sum: 0, count: 0, avg: 0 },
    confidenceToApply: { sum: 0, count: 0, avg: 0 },
    learningAdvocacy: { sum: 0, count: 0, avg: 0 },
    challengeResolution: { sum: 0, count: 0, avg: 0 },
  };

  // Get all feedback responses from subsessions
  const allFeedbackResponses = session.subSessions?.flatMap(ss =>
    ss.activities?.filter(a => a.type === 'feedback').flatMap(a => a.responses || []) || []
  ) || [];

  // Helper to accumulate Q1-Q5 scores from a response object
  const accumulateFeedbackScores = (resp) => {
    if (resp.topicRelevance) {
      feedbackQuestionAverages.topicRelevance.sum += resp.topicRelevance;
      feedbackQuestionAverages.topicRelevance.count++;
    }
    if (resp.trainerEffectiveness) {
      feedbackQuestionAverages.trainerEffectiveness.sum += resp.trainerEffectiveness;
      feedbackQuestionAverages.trainerEffectiveness.count++;
    }
    if (resp.confidenceToApply) {
      feedbackQuestionAverages.confidenceToApply.sum += resp.confidenceToApply;
      feedbackQuestionAverages.confidenceToApply.count++;
    }
    if (resp.learningAdvocacy) {
      feedbackQuestionAverages.learningAdvocacy.sum += resp.learningAdvocacy;
      feedbackQuestionAverages.learningAdvocacy.count++;
    }
    if (resp.challengeResolution) {
      feedbackQuestionAverages.challengeResolution.sum += resp.challengeResolution;
      feedbackQuestionAverages.challengeResolution.count++;
    }
  };

  // First: collect from subsession feedback activities
  allFeedbackResponses.forEach(response => {
    accumulateFeedbackScores(response.response || {});
  });

  // Fallback: if no subsession Q1-Q5 data, use overall session feedback responses
  const hasSubsessionQ5 = Object.values(feedbackQuestionAverages).some(q => q.count > 0);
  if (!hasSubsessionQ5 && session.overallFeedback?.responses?.length > 0) {
    session.overallFeedback.responses.forEach(resp => {
      accumulateFeedbackScores(resp);
    });
  }

  // Calculate averages
  Object.keys(feedbackQuestionAverages).forEach(key => {
    const q = feedbackQuestionAverages[key];
    q.avg = q.count > 0 ? (q.sum / q.count).toFixed(1) : 0;
  });

  // Check if we have 5-question format data
  const has5QuestionFeedback = Object.values(feedbackQuestionAverages).some(q => q.count > 0);

  // Calculate Performance Indices for Analytics tab
  // These are the 5 key metrics: TRS, ARS, LAI, TEI, CRI
  const performanceIndices = {
    // Topic Relevance Score (TRS) - from Q1 feedback or session analytics
    topicRelevanceIndex: feedbackQuestionAverages.topicRelevance.count > 0
      ? parseFloat(feedbackQuestionAverages.topicRelevance.avg)
      : (session.analytics?.topicRelevanceIndex || 0),

    // Action Readiness Score (ARS) - calculated from action plans with urgency weights
    actionReadinessIndex: (() => {
      // Calculate ARS from action plan responses
      const actionPlanActivities = session.subSessions?.flatMap(ss =>
        ss.activities?.filter(a => a.type === 'actionPlans' || a.type?.toLowerCase() === 'actionplans') || []
      ) || [];

      let totalARS = 0;
      let arsCount = 0;

      // Urgency weights based on by-when selections
      const urgencyWeights = {
        '7_days': 1.0,
        '15_days': 0.85,
        '30_days': 0.70,
        '60_days': 0.45,
        '90_days': 0.25,
        'not_sure': 0.15,
      };

      actionPlanActivities.forEach(activity => {
        (activity.responses || []).forEach(response => {
          const resp = response.response;
          const actions = resp?.actions || resp?.actionPlans || [];

          actions.forEach(action => {
            const byWhen = action?.byWhen || 'not_sure';
            const commitment = action?.commitment || 5; // Default to 5 if not set
            const urgencyWeight = urgencyWeights[byWhen] || 0.15;
            const commitmentFactor = commitment / 10;
            const ars = urgencyWeight * commitmentFactor;
            totalARS += ars;
            arsCount++;
          });
        });
      });

      // Also check session analytics if available
      if (arsCount === 0 && session.analytics?.actionReadinessIndex > 0) {
        return session.analytics.actionReadinessIndex;
      }

      return arsCount > 0 ? Math.round((totalARS / arsCount) * 100) / 100 : 0;
    })(),

    // Learning Advocacy Index (LAI) - from Q4 feedback or session analytics
    learningAdvocacyIndex: feedbackQuestionAverages.learningAdvocacy.count > 0
      ? parseFloat(feedbackQuestionAverages.learningAdvocacy.avg)
      : (session.analytics?.learningAdvocacyIndex || 0),

    // Trainer Effectiveness Index (TEI) - from Q2 feedback or session analytics
    trainerEffectivenessIndex: feedbackQuestionAverages.trainerEffectiveness.count > 0
      ? parseFloat(feedbackQuestionAverages.trainerEffectiveness.avg)
      : (session.analytics?.trainerEffectivenessIndex || 0),

    // Challenge Resolution Index (CRI) - from Q5 feedback or session analytics
    challengeResolutionIndex: feedbackQuestionAverages.challengeResolution.count > 0
      ? parseFloat(feedbackQuestionAverages.challengeResolution.avg)
      : (session.analytics?.challengeResolutionIndex || 0),
  };

  // Count quiz responses separately
  const quizResponses = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a => a.type === 'quiz').reduce((s, a) => s + getResponseCount(a), 0) || 0), 0) || 0;

  // Count wordCloud responses separately (as reflection responses)
  const wordCloudResponses = session.subSessions?.reduce((sum, ss) =>
    sum + (ss.activities?.filter(a => a.type === 'wordCloud' || a.type === 'word_cloud').reduce((s, a) => s + getResponseCount(a), 0) || 0), 0) || 0;

  // Count actual workbook submissions (from launchedWorkbook tracking or session workbooks)
  const actualWorkbookCount = session.workbooks?.length || 0;

  // Overall engagement snapshot
  const overallSnapshot = {
    engagementScore: avgEngagement,
    pollResponses: pollResponses, // Only show actual poll responses, not fallback
    quizResponses: quizResponses,
    wordCloudResponses: wordCloudResponses,
    workbookCount: actualWorkbookCount, // Number of workbooks uploaded, not responses
    totalChallenges: challengeResponses,
    totalActionPlans: actionPlanResponses,
    avgRating: calculatedAvgRating || session.analytics?.avgRating || session.avgRating || 0,
  };

  // Session-wise engagement data using real data
  const sessionEngagement = session.subSessions?.map((subSession, idx) => {
    // Calculate real stats from activities in this subsession
    const ssActivities = subSession.activities || [];
    const ssPolls = ssActivities.filter(a => a.type === 'poll');
    const ssPollResponses = ssPolls.reduce((sum, p) => sum + getResponseCount(p), 0);
    const ssQuizzes = ssActivities.filter(a => a.type === 'quiz');
    const ssQuizResponses = ssQuizzes.reduce((sum, q) => sum + getResponseCount(q), 0);
    // Get unique participants who responded in this subsession (must be computed first for engagement calc)
    const ssUniqueRespondents = new Set();
    ssActivities.forEach(a => {
      (a.responses || []).forEach(r => {
        if (r.participantId || r.participant) {
          ssUniqueRespondents.add(r.participantId || r.participant);
        }
      });
    });
    const ssActiveParticipants = ssUniqueRespondents.size || 0;
    const ssExpectedParticipants = totalParticipants;

    // Only count responses from launched/completed/active activities
    const ssLaunchedActivities = ssActivities.filter(a =>
      a.status === 'launched' || a.status === 'completed' || a.status === 'active'
    );
    // Use backend-computed counts from Participant records if available
    const ssTotalResponses = ssLaunchedActivities.reduce((sum, a) => {
      const actId = (a._id || a.id)?.toString();
      if (session.activityResponseCounts && actId) {
        return sum + (session.activityResponseCounts[actId] || 0);
      }
      return sum + (a.responses?.length || 0);
    }, 0);
    // Use launched activities count * total joined participants for engagement calculation
    const ssTotalPossible = ssLaunchedActivities.length * participantsForEngagement;
    const ssEngagement = ssTotalPossible > 0 ? Math.min(100, Math.round((ssTotalResponses / ssTotalPossible) * 100)) : 0;

    // Get challenges and action plans from actual responses
    const ssChallenges = ssActivities.filter(a => a.type === 'challenges');
    const ssChallengeResponses = ssChallenges.flatMap(a => a.responses || []);
    const ssActionPlans = ssActivities.filter(a => a.type === 'actionPlans' || a.type?.toLowerCase() === 'actionplans');
    const ssActionPlanResponses = ssActionPlans.flatMap(a => a.responses || []);

    // Calculate launched (completed) activities count
    const ssLaunchedActivityCount = ssLaunchedActivities.length;
    const ssTotalActivities = ssActivities.length;

    // Get feedback rating for this subsession (comprehensive rating extraction)
    const ssFeedbackActivities = ssActivities.filter(a => a.type === 'feedback');
    const ssFeedbackResponses = ssFeedbackActivities.flatMap(a => a.responses || []);
    let ssFeedbackRating = 0;
    if (ssFeedbackResponses.length > 0) {
      let ssRatingSum = 0;
      let ssRatingCount = 0;
      ssFeedbackResponses.forEach(response => {
        const resp = response.response;
        let rating = null;
        // Check for numeric rating in various formats
        if (typeof resp?.rating === 'number') {
          rating = resp.rating;
        } else if (typeof resp?.score === 'number') {
          rating = resp.score;
        } else if (typeof resp?.value === 'number') {
          rating = resp.value;
        } else if (typeof response?.rating === 'number') {
          rating = response.rating;
        }
        // Check for emoji rating
        else if (resp?.emoji || resp?.selectedEmoji) {
          rating = emojiToRating(resp.emoji || resp.selectedEmoji);
        } else if (response?.emoji) {
          rating = emojiToRating(response.emoji);
        }
        // Check if response itself is an emoji or number
        else if (typeof resp === 'string') {
          rating = emojiToRating(resp);
        } else if (typeof resp === 'number') {
          rating = resp;
        }
        // Use getFeedbackRating as fallback
        if (!rating && (resp?.emoji || response?.emoji)) {
          rating = getFeedbackRating(resp?.emoji || response?.emoji);
        }

        if (rating && rating > 0) {
          ssRatingSum += rating;
          ssRatingCount++;
        }
      });
      ssFeedbackRating = ssRatingCount > 0 ? (ssRatingSum / ssRatingCount).toFixed(1) : 0;
    }

    // Count completed activities in this subsession
    const ssCompletedActivities = ssActivities.filter(a =>
      a.status === 'completed' || a.status === 'closed' ||
      (a.status !== 'pending' && a.status !== 'draft' && (getResponseCount(a) > 0 || a.endedAt || a.completedAt))
    ).length;

    return {
      day: idx + 1,
      topic: subSession.name || subSession.title || `Section ${idx + 1}`,
      duration: (subSession.startTime && subSession.endTime)
        ? `${subSession.duration || Math.round((new Date(subSession.endTime) - new Date(subSession.startTime)) / 60000)} mins`
        : null,
      highlights: {
        launchedActivities: `${ssLaunchedActivityCount}/${ssTotalActivities}`,
        completedActivities: `${ssCompletedActivities}/${ssTotalActivities}`,
        engagementScore: `${ssEngagement}%`,
        feedbackRating: ssFeedbackRating > 0 ? `${ssFeedbackRating}/5` : '0/5',
        joinedParticipants: participantsForEngagement > 0 ? `${ssActiveParticipants}/${participantsForEngagement}` : `${ssActiveParticipants}`,
        activeParticipants: ssActiveParticipants,
        expectedParticipants: participantsForEngagement,
      },
      challenges: ssChallengeResponses.length > 0 ? [
        {
          topic: subSession.name || subSession.title,
          items: ssChallengeResponses.flatMap((r, i) => {
            // Handle various response formats for challenges
            const resp = r.response;

            // NEW: Handle array of challenges - expand each into separate items
            if (resp?.challenges && Array.isArray(resp.challenges)) {
              return resp.challenges
                .filter(c => c && c.trim()) // Remove empty challenges
                .slice(0, 10) // Limit to 10 challenges per response
                .map(challengeText => {
                  // Truncate long text for display
                  if (challengeText.length > 150) {
                    challengeText = challengeText.substring(0, 150) + '...';
                  }
                  return {
                    text: challengeText,
                    count: 1,
                  };
                });
            }

            // Handle legacy single text format
            let challengeText = null;

            // Try different possible field names - check all common formats
            if (typeof resp === 'string') {
              challengeText = resp;
            } else if (resp?.text) {
              challengeText = resp.text;
            } else if (resp?.challenge) {
              challengeText = resp.challenge;
            } else if (resp?.content) {
              challengeText = resp.content;
            } else if (resp?.answer) {
              challengeText = resp.answer;
            } else if (resp?.value) {
              challengeText = resp.value;
            } else if (Array.isArray(resp)) {
              // If response is an array of strings
              return resp
                .filter(item => typeof item === 'string' && item.trim())
                .map(text => ({
                  text: text.length > 150 ? text.substring(0, 150) + '...' : text,
                  count: 1,
                }));
            } else if (typeof resp === 'object' && resp !== null) {
              // Last resort: stringify the object or get first string value
              const firstStringValue = Object.values(resp).find(v => typeof v === 'string');
              challengeText = firstStringValue || JSON.stringify(resp).substring(0, 100);
            }

            // Also check if the response itself has the text at root level (r.text instead of r.response.text)
            if (!challengeText && r.text) {
              challengeText = r.text;
            }

            // Truncate long text for display
            if (challengeText && challengeText.length > 150) {
              challengeText = challengeText.substring(0, 150) + '...';
            }

            return challengeText ? [{
              text: challengeText,
              count: 1,
            }] : [{
              text: `Challenge ${i + 1}`,
              count: 1,
            }];
          }),
        },
      ] : [
        {
          topic: subSession.name || subSession.title,
          items: [{ text: 'No challenges reported yet', count: 0 }],
        },
      ],
      actionPlans: ssActionPlanResponses.length > 0 ? [
        {
          topic: subSession.name || subSession.title,
          items: ssActionPlanResponses.flatMap((r, i) => {
            // Handle various response formats for action plans
            const resp = r.response;

            // NEW: Handle actions array format - expand each action into separate items
            if (resp?.actions && Array.isArray(resp.actions)) {
              return resp.actions
                .filter(action => {
                  const text = action?.text || action?.action || action?.plan || (typeof action === 'string' ? action : null);
                  return text && text.trim();
                })
                .map(action => {
                  let actionText = action?.text || action?.action || action?.plan || (typeof action === 'string' ? action : '');
                  // Truncate long text for display
                  if (actionText.length > 150) {
                    actionText = actionText.substring(0, 150) + '...';
                  }
                  return {
                    text: actionText,
                    count: 1,
                  };
                });
            }

            // Handle actionPlans array format
            if (resp?.actionPlans && Array.isArray(resp.actionPlans)) {
              return resp.actionPlans
                .filter(ap => {
                  const text = typeof ap === 'string' ? ap : (ap?.text || ap?.action || ap?.plan);
                  return text && text.trim();
                })
                .map(ap => {
                  let actionText = typeof ap === 'string' ? ap : (ap?.text || ap?.action || ap?.plan || '');
                  if (actionText.length > 150) {
                    actionText = actionText.substring(0, 150) + '...';
                  }
                  return {
                    text: actionText,
                    count: 1,
                  };
                });
            }

            // Handle legacy single text format
            let actionText = null;

            // Try different possible field names - check all common formats
            if (typeof resp === 'string') {
              actionText = resp;
            } else if (resp?.text) {
              actionText = resp.text;
            } else if (resp?.action) {
              actionText = resp.action;
            } else if (resp?.actionPlan) {
              actionText = resp.actionPlan;
            } else if (resp?.plan) {
              actionText = resp.plan;
            } else if (resp?.content) {
              actionText = resp.content;
            } else if (resp?.answer) {
              actionText = resp.answer;
            } else if (resp?.value) {
              actionText = resp.value;
            } else if (Array.isArray(resp)) {
              // If response is an array of strings, expand each
              return resp
                .filter(item => typeof item === 'string' && item.trim())
                .map(text => ({
                  text: text.length > 150 ? text.substring(0, 150) + '...' : text,
                  count: 1,
                }));
            } else if (typeof resp === 'object' && resp !== null) {
              // Last resort: stringify the object or get first string value
              const firstStringValue = Object.values(resp).find(v => typeof v === 'string');
              actionText = firstStringValue || JSON.stringify(resp).substring(0, 100);
            }

            // Also check if the response itself has the text at root level (r.text instead of r.response.text)
            if (!actionText && r.text) {
              actionText = r.text;
            }

            // Truncate long text for display
            if (actionText && actionText.length > 150) {
              actionText = actionText.substring(0, 150) + '...';
            }

            return actionText ? [{
              text: actionText,
              count: 1,
            }] : [];
          }),
        },
      ] : [
        {
          topic: subSession.name || subSession.title,
          items: [{ text: 'No action plans created yet', count: 0 }],
        },
      ],
    };
  }) || [];

  // Generate Excel/CSV Report
  const generateExcel = () => {
    setIsGeneratingExcel(true);

    try {
      // Prepare CSV data
      const rows = [];

      // Header section
      rows.push(['GROUP REPORT FOR L&D']);
      rows.push(['Multi-Day Training Engagement, Challenges & Action-Plan Insights']);
      rows.push([]);

      // Program Information
      rows.push(['PROGRAM INFORMATION']);
      rows.push(['Training Program', programInfo.title]);
      rows.push(['Mode', programInfo.mode]);
      rows.push(['Duration', programInfo.duration]);
      rows.push(['Facilitator', programInfo.facilitator]);
      rows.push(['Organization', programInfo.organization]);
      rows.push(['Total Expected Participants', programInfo.totalParticipants]);
      rows.push(['Active Participants', programInfo.activeParticipants]);
      rows.push(['Date', programInfo.dateRange]);
      rows.push(['Status', session?.status || 'N/A']);
      rows.push([]);

      // Overall Engagement Snapshot
      rows.push(['OVERALL ENGAGEMENT SNAPSHOT']);
      rows.push(['Metric', 'Value']);
      rows.push(['Overall Engagement Score', `${overallSnapshot.engagementScore}%`]);
      if (overallSnapshot.pollResponses > 0) {
        rows.push(['Poll Responses', overallSnapshot.pollResponses]);
      }
      if (overallSnapshot.quizResponses > 0) {
        rows.push(['Quiz Responses', overallSnapshot.quizResponses]);
      }
      if (overallSnapshot.wordCloudResponses > 0) {
        rows.push(['Word Cloud Responses', overallSnapshot.wordCloudResponses]);
      }
      if (overallSnapshot.workbookCount > 0) {
        rows.push(['Workbooks Available', overallSnapshot.workbookCount]);
      }
      rows.push(['Total Challenges Captured', overallSnapshot.totalChallenges]);
      rows.push(['Total Action Plans Created', overallSnapshot.totalActionPlans]);
      rows.push(['Session Rating (Avg)', `${overallSnapshot.avgRating}/5`]);
      rows.push([]);

      // Session-wise Engagement
      rows.push(['SESSION-WISE ENGAGEMENT']);
      rows.push(['Session', 'Engagement %', 'Responses', 'Participation Rate']);
      sessionEngagement.forEach(se => {
        const name = se.topic || se.name || `Section ${se.day || ''}`;
        const engagement = se.highlights?.engagementScore || '0%';
        const responses = `'${se.highlights?.launchedActivities || '0/0'} activities`;
        const active = se.highlights?.activeParticipants || 0;
        const expected = se.highlights?.expectedParticipants || 0;
        const participationRate = expected > 0 ? `${Math.round((active / expected) * 100)}%` : '0%';
        rows.push([name, engagement, responses, participationRate]);
      });
      rows.push([]);

      // All Challenges - extract from sessionEngagement
      const challengesData = sessionEngagement.filter(se =>
        se.challenges && se.challenges.length > 0 && se.challenges[0].items?.some(item => item.count > 0)
      );
      if (challengesData.length > 0) {
        rows.push(['ALL CHALLENGES']);
        challengesData.forEach(se => {
          se.challenges.forEach(challenge => {
            rows.push([`Topic: ${challenge.topic}`]);
            rows.push(['Challenges Reported:']);
            challenge.items.filter(item => item.count > 0).forEach((item, idx) => {
              rows.push([`  ${idx + 1}. "${item.text}"`]);
            });
          });
        });
        rows.push([]);
      }

      // All Action Plans - extract from sessionEngagement
      const actionPlansData = sessionEngagement.filter(se =>
        se.actionPlans && se.actionPlans.length > 0 && se.actionPlans[0].items?.some(item => item.count > 0)
      );
      if (actionPlansData.length > 0) {
        rows.push(['ALL ACTION PLANS']);
        actionPlansData.forEach(se => {
          se.actionPlans.forEach(actionPlan => {
            rows.push([`Topic: ${actionPlan.topic}`]);
            rows.push(['Action Plans:']);
            actionPlan.items.filter(item => item.count > 0).forEach((item, idx) => {
              rows.push([`  ${idx + 1}. ${item.text}`]);
            });
          });
        });
        rows.push([]);
      }

      // Participant Feedback Summary
      if (totalFeedbackResponses > 0) {
        rows.push(['PARTICIPANT FEEDBACK SUMMARY']);
        rows.push(['Rating', 'Count', 'Percentage']);
        const feedbackItems = [
          { label: 'Excited (5)', count: emojiDistribution.excited },
          { label: 'Satisfied (4)', count: emojiDistribution.satisfied },
          { label: 'Neutral (3)', count: emojiDistribution.neutral },
          { label: 'Disappointed (2)', count: emojiDistribution.disappointed },
          { label: 'Very Disappointed (1)', count: emojiDistribution.veryDisappointed },
        ];
        feedbackItems.forEach(item => {
          const percentage = totalFeedbackResponses > 0 ? Math.round((item.count / totalFeedbackResponses) * 100) : 0;
          rows.push([item.label, item.count, `${percentage}%`]);
        });
        rows.push([]);
      }

      // Convert rows to CSV string
      const csvContent = rows.map(row =>
        row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma or newline
          const cellStr = String(cell || '');
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      ).join('\n');

      // Create and download file
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${session?.name || 'Session'}_Report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Excel:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  // Generate PDF Report using backend API.
  // includeAI: the standard report PDF embeds the AI Impact Report as extra
  // pages when one exists (or can be generated) for the session.
  const generatePDF = async () => {
    setIsGeneratingPDF(true);

    try {
      await exportService.exportSessionAnalyticsReportPDF(sessionId, session?.code, { includeAI: true });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Download every participant's individual report bundled into a single ZIP.
  // Server generates each PDF sequentially, so this can take 30-60s for a large
  // cohort — we show a loading state and a heads-up toast.
  const handleDownloadAllReportsZip = async () => {
    if (isGeneratingZip) return;
    setIsGeneratingZip(true);
    const loadingId = toast.loading('Preparing all participant reports… this can take up to a minute.');
    try {
      await exportService.exportAllParticipantReportsZip(sessionId, session?.name);
      toast.success('All participant reports downloaded', { id: loadingId });
    } catch (error) {
      console.error('ZIP export error:', error);
      toast.error(error.message || 'Failed to download reports', { id: loadingId });
    } finally {
      setIsGeneratingZip(false);
    }
  };

  // Download complete session + AI report as styled PDF (uses backend Puppeteer)
  const handleDownloadAiSessionReport = async () => {
    if (!aiReport || aiReport.error) return;
    setIsAiPdfGenerating(true);
    try {
      await exportService.exportSessionAnalyticsReportPDF(sessionId, session?.code, { includeAI: true });
      toast.success('Report downloaded');
    } catch (error) {
      console.error('AI session PDF error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsAiPdfGenerating(false);
    }
  };


  return (
    <DashboardLayout title="Session Report">
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors group"
        >
          <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back</span>
        </button>

        {/* Header - Responsive layout */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-1 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">{session.name}</h1>
              <Badge variant={session.status} dot={session.status === 'live'}>
                {session.status}
              </Badge>
            </div>
            <p className="text-xs sm:text-sm text-neutral-600">
              {(() => {
                const actualStartTime = session.startTime || session.actualStartTime;
                const actualEndTime = session.endTime || session.actualEndTime;

                // Check if multi-day session
                const isMultiDay = actualStartTime && actualEndTime &&
                  new Date(actualStartTime).toISOString().slice(0, 10) !== new Date(actualEndTime).toISOString().slice(0, 10);

                if (isMultiDay) {
                  // Multi-day: show date range and daily time
                  const startDateStr = formatDateWithTimezone(actualStartTime, { day: 'numeric', month: 'short' });
                  const endDateStr = formatDateWithTimezone(actualEndTime, { day: 'numeric', month: 'short', year: 'numeric' });
                  const startTimeStr = formatTimeWithTimezone(actualStartTime);
                  const endTimeStr = formatTimeWithTimezone(actualEndTime);
                  return `${startDateStr} - ${endDateStr} • ${startTimeStr} - ${endTimeStr} (daily)`;
                }

                // Single day session
                const dateStr = session.scheduledDate
                  ? formatDateWithTimezone(session.scheduledDate)
                  : (actualStartTime ? formatDateWithTimezone(actualStartTime) : 'Not scheduled');

                if (actualStartTime && actualEndTime) {
                  return `${dateStr} • ${formatTimeWithTimezone(actualStartTime)} - ${formatTimeWithTimezone(actualEndTime)}`;
                }
                if (actualStartTime) {
                  return `${dateStr} • Started: ${formatTimeWithTimezone(actualStartTime)}`;
                }
                return dateStr;
              })()}
            </p>
            {/* Session Code and QR Code Link */}
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs sm:text-sm text-neutral-500">Code: <span className="font-bold text-primary-600">{session.code}</span></p>
              <button
                onClick={() => setShowQRModal(true)}
                className="text-primary-600 hover:text-primary-700 text-xs sm:text-sm flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                <span className="hidden sm:inline">Show QR Code</span>
                <span className="sm:hidden">QR</span>
              </button>
            </div>
          </div>
          {/* Export Buttons - below on mobile, right-aligned on desktop */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={generateExcel} disabled={isGeneratingExcel}>
              {isGeneratingExcel ? (
                <>
                  <svg className="w-4 h-4 mr-1 sm:mr-2 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="hidden sm:inline">Exporting...</span>
                  <span className="sm:hidden text-xs">Export...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Excel
                </>
              )}
            </Button>
            <Button variant="primary" size="sm" onClick={generatePDF} disabled={isGeneratingPDF}>
              {isGeneratingPDF ? (
                <>
                  <svg className="w-4 h-4 mr-1 sm:mr-2 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="hidden sm:inline">Generating...</span>
                  <span className="sm:hidden text-xs">PDF...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="hidden sm:inline">PDF Report</span>
                  <span className="sm:hidden">PDF</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Tabs - Horizontally scrollable on mobile */}
        <div className="border-b border-neutral-200 -mx-3 sm:-mx-0 px-3 sm:px-0">
          <div className="flex gap-1 sm:gap-6 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-2 sm:px-1 text-sm font-medium transition-colors relative whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'text-primary-600'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab - Group Report Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Session Information Card - Matching SessionDetailPage Style */}
            <Card>
              <div className="p-4 sm:p-6 space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Session Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="flex items-center gap-3 sm:block">
                    <p className="text-xs sm:text-sm text-neutral-600 min-w-[100px] sm:min-w-0">Training Program</p>
                    <p className="font-medium text-sm sm:text-base text-neutral-900">{programInfo.title}</p>
                  </div>
                  <div className="flex items-center gap-3 sm:block">
                    <p className="text-xs sm:text-sm text-neutral-600 min-w-[100px] sm:min-w-0">Mode</p>
                    <p className="font-medium text-sm sm:text-base text-neutral-900">{programInfo.mode}</p>
                  </div>
                  <div className="flex items-center gap-3 sm:block">
                    <p className="text-xs sm:text-sm text-neutral-600 min-w-[100px] sm:min-w-0">Facilitator</p>
                    <p className="font-medium text-sm sm:text-base text-neutral-900">{programInfo.facilitator}</p>
                  </div>
                  <div className="flex items-center gap-3 sm:block">
                    <p className="text-xs sm:text-sm text-neutral-600 min-w-[100px] sm:min-w-0">Hosted By</p>
                    <p className="font-medium text-sm sm:text-base text-neutral-900">{programInfo.hostedBy}</p>
                  </div>
                  <div className="flex items-center gap-3 sm:block">
                    <p className="text-xs sm:text-sm text-neutral-600 min-w-[100px] sm:min-w-0">Total Participants</p>
                    <p className="font-medium text-sm sm:text-base text-neutral-900">{programInfo.totalParticipants}</p>
                  </div>
                  <div className="flex items-center gap-3 sm:block">
                    <p className="text-xs sm:text-sm text-neutral-600 min-w-[100px] sm:min-w-0">Active Participants</p>
                    <p className="font-medium text-sm sm:text-base text-neutral-900">{programInfo.activeParticipants}</p>
                  </div>
                  <div className="flex items-center gap-3 sm:block">
                    <p className="text-xs sm:text-sm text-neutral-600 min-w-[100px] sm:min-w-0">Session Code</p>
                    <p className="font-bold text-sm sm:text-base text-primary-600">{session.code}</p>
                  </div>
                  <div className="flex items-center gap-3 sm:block">
                    <p className="text-xs sm:text-sm text-neutral-600 min-w-[100px] sm:min-w-0">Activities</p>
                    <p className="font-medium text-sm sm:text-base text-neutral-900">{launchedActivities}/{totalCreatedActivities}</p>
                  </div>
                </div>
                {session.description && (
                  <div className="pt-2 border-t border-neutral-100">
                    <p className="text-sm text-neutral-600 mb-1">Description</p>
                    <p className="text-neutral-900">{session.description}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* 1. OVERALL ENGAGEMENT SNAPSHOT */}
            <Card padding="lg">
              <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-3 sm:mb-4">
                Overall Engagement Snapshot
              </h3>
              <div className="space-y-2.5 sm:space-y-3">
                <div className="flex items-start sm:items-center gap-2">
                  <span className="text-primary-600 font-bold mt-0.5 sm:mt-0">•</span>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                    <span className="text-sm sm:text-base text-neutral-700">Overall Engagement Score:</span>
                    <span className="font-bold text-sm sm:text-base text-neutral-900">{overallSnapshot.engagementScore}%</span>
                  </div>
                </div>
                {overallSnapshot.pollResponses > 0 && (
                  <div className="flex items-start sm:items-center gap-2">
                    <span className="text-primary-600 font-bold mt-0.5 sm:mt-0">•</span>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                      <span className="text-sm sm:text-base text-neutral-700">Poll Responses Collected:</span>
                      <span className="font-bold text-sm sm:text-base text-neutral-900">{overallSnapshot.pollResponses} across all sessions</span>
                    </div>
                  </div>
                )}
                {overallSnapshot.quizResponses > 0 && (
                  <div className="flex items-start sm:items-center gap-2">
                    <span className="text-primary-600 font-bold mt-0.5 sm:mt-0">•</span>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                      <span className="text-sm sm:text-base text-neutral-700">Quiz Responses Collected:</span>
                      <span className="font-bold text-sm sm:text-base text-neutral-900">{overallSnapshot.quizResponses} across all sessions</span>
                    </div>
                  </div>
                )}
                {overallSnapshot.wordCloudResponses > 0 && (
                  <div className="flex items-start sm:items-center gap-2">
                    <span className="text-primary-600 font-bold mt-0.5 sm:mt-0">•</span>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                      <span className="text-sm sm:text-base text-neutral-700">Word Cloud Responses:</span>
                      <span className="font-bold text-sm sm:text-base text-neutral-900">{overallSnapshot.wordCloudResponses}</span>
                    </div>
                  </div>
                )}
                {overallSnapshot.workbookCount > 0 && (
                  <div className="flex items-start sm:items-center gap-2">
                    <span className="text-primary-600 font-bold mt-0.5 sm:mt-0">•</span>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                      <span className="text-sm sm:text-base text-neutral-700">Workbooks Available:</span>
                      <span className="font-bold text-sm sm:text-base text-neutral-900">{overallSnapshot.workbookCount}</span>
                    </div>
                  </div>
                )}
                <div className="flex items-start sm:items-center gap-2">
                  <span className="text-primary-600 font-bold mt-0.5 sm:mt-0">•</span>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                    <span className="text-sm sm:text-base text-neutral-700">Total Challenges Captured:</span>
                    <span className="font-bold text-sm sm:text-base text-neutral-900">{overallSnapshot.totalChallenges} (topic-wise)</span>
                  </div>
                </div>
                <div className="flex items-start sm:items-center gap-2">
                  <span className="text-primary-600 font-bold mt-0.5 sm:mt-0">•</span>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                    <span className="text-sm sm:text-base text-neutral-700">Total Action Plans Created:</span>
                    <span className="font-bold text-sm sm:text-base text-neutral-900">{overallSnapshot.totalActionPlans}</span>
                  </div>
                </div>
                <div className="flex items-start sm:items-center gap-2">
                  <span className="text-primary-600 font-bold mt-0.5 sm:mt-0">•</span>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1">
                    <span className="text-sm sm:text-base text-neutral-700">Session Rating (Avg.):</span>
                    <span className="font-bold text-sm sm:text-base text-neutral-900 flex items-center gap-1">
                      <span className="text-yellow-500">⭐</span> {overallSnapshot.avgRating} / 5
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Session-wise Engagement Summary */}
            <Card padding="lg">
              <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-3 sm:mb-4">Session-wise Engagement Summary</h3>
              {sessionEngagement.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-neutral-500">No sub-session data available yet</p>
                  <p className="text-sm text-neutral-400 mt-1">Create sub-sessions with activities to see engagement data</p>
                </div>
              ) : (
              <div className="space-y-8">
                {sessionEngagement.map((sessionData, idx) => (
                  <div key={sessionData.day} className={idx > 0 ? 'pt-8 border-t border-neutral-200' : ''}>
                    {/* Sub-session Header */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <span className="px-2 sm:px-3 py-1 bg-primary-100 text-primary-700 text-xs sm:text-sm font-bold rounded-lg">
                        Sub-session {sessionData.day}
                      </span>
                      <h4 className="font-semibold text-sm sm:text-base text-neutral-900">{sessionData.topic}</h4>
                      {sessionData.duration && <span className="text-xs sm:text-sm text-neutral-500">({sessionData.duration})</span>}
                    </div>

                    {/* Engagement Highlights */}
                    <div className="bg-neutral-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                      <p className="text-xs sm:text-sm font-semibold text-neutral-700 mb-2 sm:mb-3">Engagement Highlights</p>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3">
                        <div className="flex items-center gap-3 sm:block sm:text-center p-2 sm:p-0 bg-white sm:bg-transparent rounded-lg sm:rounded-none">
                          <p className="text-base sm:text-lg font-bold text-blue-600">{sessionData.highlights.launchedActivities}</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">Launched Activities</p>
                        </div>
                        <div className="flex items-center gap-3 sm:block sm:text-center p-2 sm:p-0 bg-white sm:bg-transparent rounded-lg sm:rounded-none">
                          <p className="text-base sm:text-lg font-bold text-green-600">{sessionData.highlights.engagementScore}</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">Engagement Score</p>
                        </div>
                        <div className="flex items-center gap-3 sm:block sm:text-center p-2 sm:p-0 bg-white sm:bg-transparent rounded-lg sm:rounded-none">
                          <p className="text-base sm:text-lg font-bold text-yellow-600">{sessionData.highlights.feedbackRating}</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">Feedback Rating</p>
                        </div>
                        <div className="flex items-center gap-3 sm:block sm:text-center p-2 sm:p-0 bg-white sm:bg-transparent rounded-lg sm:rounded-none">
                          <p className="text-base sm:text-lg font-bold text-purple-600">{sessionData.highlights.activeParticipants}</p>
                          <p className="text-[10px] sm:text-xs text-neutral-500">{sessionData.highlights.activeParticipants} responded out of {sessionData.highlights.expectedParticipants} joined</p>
                        </div>
                      </div>
                    </div>

                    {/* Challenges & Action Plans Side by Side */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Challenges */}
                      <div className="border border-red-200 rounded-lg">
                        <div className="bg-red-50 px-4 py-3 border-b border-red-200 rounded-t-lg">
                          <p className="font-semibold text-red-800">All Challenges</p>
                        </div>
                        <div className="p-4 space-y-4 max-h-64 overflow-y-auto">
                          {sessionData.challenges.map((challenge, cIdx) => (
                            <div key={cIdx}>
                              <p className="text-sm font-semibold text-primary-600 mb-2">Topic: {challenge.topic}</p>
                              <p className="text-xs text-neutral-500 mb-1">Challenges Reported:</p>
                              <ol className="space-y-1.5">
                                {challenge.items.filter(item => item.count > 0).map((item, iIdx) => (
                                  <li key={iIdx} className="text-sm text-neutral-700 flex items-start gap-2">
                                    <span className="text-neutral-400">{iIdx + 1}.</span>
                                    <span>"{item.text}"</span>
                                  </li>
                                ))}
                                {challenge.items.filter(item => item.count > 0).length === 0 && (
                                  <li className="text-sm text-neutral-500 italic">No challenges reported yet</li>
                                )}
                              </ol>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Plans */}
                      <div className="border border-green-200 rounded-lg">
                        <div className="bg-green-50 px-4 py-3 border-b border-green-200 rounded-t-lg">
                          <p className="font-semibold text-green-800">All Action Plans</p>
                        </div>
                        <div className="p-4 space-y-4 max-h-64 overflow-y-auto">
                          {sessionData.actionPlans.map((plan, pIdx) => (
                            <div key={pIdx}>
                              <p className="text-sm font-semibold text-primary-600 mb-2">Topic: {plan.topic}</p>
                              <p className="text-xs text-neutral-500 mb-1">Action Plans:</p>
                              <ol className="space-y-1.5">
                                {plan.items.filter(item => item.count > 0).map((item, iIdx) => (
                                  <li key={iIdx} className="text-sm text-neutral-700 flex items-start gap-2">
                                    <span className="text-neutral-400">{iIdx + 1}.</span>
                                    <span>{item.text}</span>
                                  </li>
                                ))}
                                {plan.items.filter(item => item.count > 0).length === 0 && (
                                  <li className="text-sm text-neutral-500 italic">No action plans created yet</li>
                                )}
                              </ol>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </Card>

            {/* Micro simulations run in this session (renders nothing if none) */}
            <MicroSimSessionSection sessionId={sessionId} />

          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <Card title="Session Timeline" padding="lg">
            <div className="space-y-3">
              {/* Session Created */}
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0 bg-green-500"></div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs sm:text-sm font-medium text-neutral-900">Session Created</p>
                    <span className="text-[10px] sm:text-xs text-neutral-500">
                      {session.createdAt ? formatActualDateTime(session.createdAt) : '-'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Session Started - only show when session has actually started */}
              {(session.actualStartTime || session.status === 'live' || session.status === 'completed') && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0 bg-blue-500"></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs sm:text-sm font-medium text-neutral-900">Session Started</p>
                      <span className="text-[10px] sm:text-xs text-neutral-500">
                        {session.actualStartTime
                          ? formatActualDateTime(session.actualStartTime)
                          : (() => {
                              // Fallback for sessions launched before actualStartTime was tracked:
                              // use the earliest activity launchedAt as the session start time
                              const firstLaunch = session.subSessions?.flatMap(ss => ss.activities || [])
                                .map(a => a.launchedAt || a.startedAt)
                                .filter(Boolean)
                                .sort()[0];
                              return firstLaunch
                                ? formatActualDateTime(firstLaunch)
                                : session.startTime
                                  ? formatLocalDateTime(session.startTime)
                                  : '-';
                            })()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* SubSessions with Activities */}
              {session.subSessions?.map((subSession, subIndex) => {
                const completedActivitiesCount = subSession.activities?.filter(a => a.status === 'completed').length || 0;
                const totalActivitiesCount = subSession.activities?.length || 0;

                return (
                  <div key={subSession._id || subIndex} className="border-l-2 border-neutral-200 ml-1 pl-3 sm:pl-4">
                    {/* SubSession Header */}
                    <div className="flex items-center justify-between mb-2 -ml-[14px] sm:-ml-[18px]">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0"></div>
                        <p className="text-xs sm:text-sm font-semibold text-neutral-900 truncate">
                          {subSession.name || subSession.title || `Sub-session ${subIndex + 1}`}
                        </p>
                      </div>
                      <Badge variant={completedActivitiesCount === totalActivitiesCount && totalActivitiesCount > 0 ? 'success' : 'default'} size="sm">
                        {completedActivitiesCount}/{totalActivitiesCount}
                      </Badge>
                    </div>

                    {/* Activities under this SubSession */}
                    <div className="space-y-2 sm:space-y-1.5 ml-1 sm:ml-2">
                      {subSession.activities?.length > 0 ? (
                        subSession.activities.map((activity, actIndex) => {
                          // For completed sessions, treat all launched activities as completed
                          const isSessionCompleted = session.status === 'completed' || session.status === 'expired';
                          const wasLaunched = activity.status === 'launched' || activity.status === 'completed' || activity.status === 'active' || activity.startedAt || activity.launchedAt;
                          const isActive = !isSessionCompleted && (activity.status === 'active' || activity.status === 'in_progress' || activity.status === 'launched');
                          const isActivityCompleted = activity.status === 'completed' || (isSessionCompleted && wasLaunched);
                          const notLaunched = !wasLaunched && activity.status !== 'completed';

                          // Get activity times
                          const launchedTime = activity.launchedAt || activity.startedAt;
                          const endedTime = activity.endedAt || activity.completedAt;

                          // Get activity label
                          const activityLabels = {
                            quiz: 'Quiz',
                            poll: 'Poll',
                            wordCloud: 'Word Cloud',
                            word_cloud: 'Word Cloud',
                            challenges: 'Challenges',
                            actionPlans: 'Action Plans',
                            openQuestion: 'Open Question',
                            feedback: 'Feedback',
                          };
                          const activityLabel = activityLabels[activity.type] || activity.type;

                          return (
                            <div key={activity._id || actIndex} className="flex items-start gap-2">
                              <div
                                className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                                  isActive ? 'bg-green-500 animate-pulse' : isActivityCompleted ? 'bg-blue-500' : 'bg-neutral-300'
                                }`}
                              ></div>
                              <div className="flex-1 min-w-0">
                                {/* Desktop: single row */}
                                <div className="hidden sm:flex items-center justify-between">
                                  <p className="text-xs text-neutral-700 flex items-center gap-1.5">
                                    <span className={`w-5 h-5 flex-shrink-0 rounded flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
                                      {getActivityIcon(activity.type, 'sm')}
                                    </span>
                                    <span>{activity.title || activity.name || activityLabel}</span>
                                  </p>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    {launchedTime && (
                                      <span className="text-[10px] text-green-600">
                                        {formatActualTime(launchedTime)}
                                      </span>
                                    )}
                                    {launchedTime && endedTime && (
                                      <span className="text-[10px] text-neutral-400">-</span>
                                    )}
                                    {endedTime && (
                                      <span className="text-[10px] text-blue-600">
                                        {formatActualTime(endedTime)}
                                      </span>
                                    )}
                                    {!launchedTime && !endedTime && (
                                      <span className="text-[10px] text-neutral-400">
                                        {notLaunched ? 'Not launched' : '-'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {/* Mobile: stacked layout */}
                                <div className="sm:hidden">
                                  <p className="text-xs text-neutral-700 flex items-center gap-1.5">
                                    <span className={`w-5 h-5 flex-shrink-0 rounded flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
                                      {getActivityIcon(activity.type, 'sm')}
                                    </span>
                                    <span className="truncate">{activity.title || activity.name || activityLabel}</span>
                                  </p>
                                  <div className="flex items-center gap-1 mt-0.5 ml-[26px]">
                                    {launchedTime && (
                                      <span className="text-[10px] text-green-600">
                                        {formatActualTime(launchedTime)}
                                      </span>
                                    )}
                                    {launchedTime && endedTime && (
                                      <span className="text-[10px] text-neutral-400">-</span>
                                    )}
                                    {endedTime && (
                                      <span className="text-[10px] text-blue-600">
                                        {formatActualTime(endedTime)}
                                      </span>
                                    )}
                                    {!launchedTime && !endedTime && (
                                      <span className="text-[10px] text-neutral-400">
                                        {notLaunched ? 'Not launched' : '-'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-[10px] text-neutral-400 italic">No activities</p>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Session End (if completed) */}
              {(session.status === 'completed' || session.status === 'expired') && (session.actualEndTime || session.endTime) && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0 bg-neutral-400"></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs sm:text-sm font-medium text-neutral-900">Session Ended</p>
                      <span className="text-[10px] sm:text-xs text-neutral-500">
                        {session.actualEndTime
                          ? formatActualDateTime(session.actualEndTime)
                          : formatLocalDateTime(session.endTime)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {(!session.subSessions || session.subSessions.length === 0) && !session.startTime && (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-neutral-100 rounded-full mb-2">
                    <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-neutral-600">No timeline events yet</p>
                  <p className="text-xs text-neutral-500">Events will appear here as the session progresses.</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Activities Tab */}
        {activeTab === 'activities' && (
          <div className="space-y-6">
            {/* Activity Performance Summary Card */}
            <Card title="Activity Performance Summary" padding="lg">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4">
                <div className="flex items-center gap-3 sm:block p-3 sm:p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-neutral-600 sm:mb-1 min-w-[100px] sm:min-w-0">Total Created</p>
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">{totalCreatedActivities}</p>
                </div>
                <div className="flex items-center gap-3 sm:block p-3 sm:p-4 bg-orange-50 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-neutral-600 sm:mb-1 min-w-[100px] sm:min-w-0">Launched</p>
                  <p className="text-lg sm:text-2xl font-bold text-orange-600">{launchedActivities}</p>
                </div>
                <div className="flex items-center gap-3 sm:block p-3 sm:p-4 bg-green-50 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-neutral-600 sm:mb-1 min-w-[100px] sm:min-w-0">Completed</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{completedActivities}</p>
                </div>
                <div className="flex items-center gap-3 sm:block p-3 sm:p-4 bg-purple-50 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-neutral-600 sm:mb-1 min-w-[100px] sm:min-w-0">Avg Participation</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-600">{overallSnapshot.engagementScore}%</p>
                </div>
              </div>
            </Card>

            {/* Sub-sessions with Activities */}
            {(!session.subSessions || session.subSessions.length === 0) ? (
              <Card padding="lg">
                <div className="text-center py-8">
                  <p className="text-neutral-500">No sub-sessions or activities available yet</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                {session.subSessions.map((subSession, idx) => {
                  const ssCompletedActivities = subSession.activities?.filter(a => a.status === 'completed').length || 0;
                  const ssTotalActivities = subSession.activities?.length || 0;
                  // Use total joined participants as denominator for all activities
                  const ssParticipants = participantsForEngagement;

                  // Activity type labels
                  const getActivityLabel = (type) => {
                    const labels = {
                      quiz: 'Quiz',
                      poll: 'Poll',
                      wordCloud: 'Word Cloud',
                      challenges: 'Challenges',
                      actionPlans: 'Action Plans',
                      openQuestion: 'Open Question',
                      feedback: 'Feedback',
                    };
                    return labels[type] || type;
                  };

                  return (
                    <Card key={subSession._id || subSession.id || idx}>
                      {/* Sub-session Header */}
                      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-neutral-200">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-600 font-bold text-xs sm:text-sm">{idx + 1}</span>
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-semibold text-sm sm:text-base text-neutral-900 truncate">
                              {subSession.name || subSession.title || `Sub-session ${idx + 1}`}
                            </h4>
                            <p className="text-[10px] sm:text-xs text-neutral-500">
                              {(subSession.startTime && subSession.endTime) ? `${subSession.duration || Math.round((new Date(subSession.endTime) - new Date(subSession.startTime)) / 60000)} min • ` : ''}{ssTotalActivities} activities
                            </p>
                          </div>
                        </div>
                        <Badge variant={ssCompletedActivities === ssTotalActivities && ssTotalActivities > 0 ? 'success' : 'default'} size="sm">
                          <span className="hidden sm:inline">{ssCompletedActivities}/{ssTotalActivities} completed</span>
                          <span className="sm:hidden">{ssCompletedActivities}/{ssTotalActivities}</span>
                        </Badge>
                      </div>

                      {/* Activities - Table on desktop, Cards on mobile */}
                      <div className="p-3 sm:p-4">
                        {subSession.activities?.length > 0 ? (
                          <>
                            {/* Desktop Table */}
                            <div className="hidden sm:block overflow-x-auto">
                              <table className="w-full min-w-[800px]">
                                <thead>
                                  <tr className="border-b border-neutral-100">
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Activity</th>
                                    <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Type</th>
                                    <th className="text-center py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Responses</th>
                                    <th className="text-center py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Participation</th>
                                    <th className="text-center py-2 px-3 text-xs font-semibold text-neutral-500 uppercase">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {subSession.activities.map((activity, aIdx) => {
                                    const actId = (activity._id || activity.id)?.toString();
                                    const responseCount = (session.activityResponseCounts && actId)
                                      ? (session.activityResponseCounts[actId] || 0)
                                      : (activity.responses?.length || 0);
                                    const participationRate = ssParticipants > 0
                                      ? Math.min(100, Math.round((responseCount / ssParticipants) * 100))
                                      : 0;
                                    const isCompleted = activity.status === 'completed';
                                    const wasLaunched = activity.status === 'launched' || activity.status === 'completed' || activity.status === 'active' || activity.startedAt || activity.launchedAt;
                                    const isActive = activity.status === 'active' || activity.status === 'in_progress' || activity.status === 'launched';

                                    let statusLabel = 'Not Launched';
                                    let statusVariant = 'default';
                                    if (isCompleted) { statusLabel = 'Done'; statusVariant = 'success'; }
                                    else if (isActive) { statusLabel = 'Active'; statusVariant = 'live'; }
                                    else if (wasLaunched) { statusLabel = 'Ended'; statusVariant = 'default'; }

                                    return (
                                      <tr key={activity._id || aIdx} className="border-b border-neutral-50 hover:bg-neutral-50">
                                        <td className="py-3 px-3">
                                          <div className="flex items-center gap-2">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
                                              {getActivityIcon(activity.type, 'sm')}
                                            </div>
                                            <span className="text-sm font-medium text-neutral-900">
                                              {activity.title || activity.name || getActivityLabel(activity.type)}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-3 px-3">
                                          <span className="text-xs px-2 py-1 bg-neutral-100 rounded-full text-neutral-600">
                                            {getActivityLabel(activity.type)}
                                          </span>
                                        </td>
                                        <td className="py-3 px-3 text-center">
                                          <span className="text-sm font-semibold text-neutral-900">{responseCount}</span>
                                          <span className="text-xs text-neutral-500">/{ssParticipants}</span>
                                        </td>
                                        <td className="py-3 px-3">
                                          <div className="flex items-center justify-center gap-2">
                                            <div className="w-16 bg-neutral-200 rounded-full h-1.5">
                                              <div
                                                className={`h-1.5 rounded-full ${isCompleted ? 'bg-green-500' : wasLaunched ? 'bg-blue-500' : 'bg-neutral-300'}`}
                                                style={{ width: `${Math.min(100, participationRate)}%` }}
                                              ></div>
                                            </div>
                                            <span className="text-xs font-medium text-neutral-600">{participationRate}%</span>
                                          </div>
                                        </td>
                                        <td className="py-3 px-3 text-center">
                                          <Badge variant={statusVariant} size="sm">{statusLabel}</Badge>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="sm:hidden space-y-2">
                              {subSession.activities.map((activity, aIdx) => {
                                const actId = (activity._id || activity.id)?.toString();
                                const responseCount = (session.activityResponseCounts && actId)
                                  ? (session.activityResponseCounts[actId] || 0)
                                  : (activity.responses?.length || 0);
                                const participationRate = ssParticipants > 0
                                  ? Math.min(100, Math.round((responseCount / ssParticipants) * 100))
                                  : 0;
                                const isCompleted = activity.status === 'completed';
                                const wasLaunched = activity.status === 'launched' || activity.status === 'completed' || activity.status === 'active' || activity.startedAt || activity.launchedAt;
                                const isActive = activity.status === 'active' || activity.status === 'in_progress' || activity.status === 'launched';

                                let statusLabel = 'Not Launched';
                                let statusVariant = 'default';
                                if (isCompleted) { statusLabel = 'Done'; statusVariant = 'success'; }
                                else if (isActive) { statusLabel = 'Active'; statusVariant = 'live'; }
                                else if (wasLaunched) { statusLabel = 'Ended'; statusVariant = 'default'; }

                                return (
                                  <div key={activity._id || aIdx} className="border border-neutral-100 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityTypeColor(activity.type)}`}>
                                          {getActivityIcon(activity.type, 'sm')}
                                        </div>
                                        <span className="text-sm font-medium text-neutral-900 truncate">
                                          {activity.title || activity.name || getActivityLabel(activity.type)}
                                        </span>
                                      </div>
                                      <Badge variant={statusVariant} size="sm">{statusLabel}</Badge>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-neutral-500">
                                      <span className="px-2 py-0.5 bg-neutral-100 rounded-full text-neutral-600">{getActivityLabel(activity.type)}</span>
                                      <span><span className="font-semibold text-neutral-900">{responseCount}</span>/{ssParticipants} responses</span>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                      <div className="flex-1 bg-neutral-200 rounded-full h-1.5">
                                        <div
                                          className={`h-1.5 rounded-full ${isCompleted ? 'bg-green-500' : wasLaunched ? 'bg-blue-500' : 'bg-neutral-300'}`}
                                          style={{ width: `${Math.min(100, participationRate)}%` }}
                                        ></div>
                                      </div>
                                      <span className="text-xs font-medium text-neutral-600">{participationRate}%</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-neutral-400 text-center py-4">No activities in this sub-session</p>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Challenge Analytics Section */}
            {(() => {
              // Calculate Challenge Density
              const challengeDensity = session.subSessions?.map(ss => {
                const challengesActivity = ss.activities?.find(a => a.type === 'challenges');
                const responses = challengesActivity?.responses || [];
                const uniqueParticipants = new Set();
                responses.forEach(r => {
                  if (r.participant) uniqueParticipants.add(r.participant.toString());
                });
                return {
                  subSessionId: ss._id,
                  subSessionName: ss.name,
                  challengeCount: responses.length,
                  participantCount: uniqueParticipants.size
                };
              })
                .filter(d => d.challengeCount > 0)
                .sort((a, b) => b.challengeCount - a.challengeCount)
                .slice(0, 5) || [];

              // Calculate Top Challenge Themes (word frequency)
              const allChallengeTexts = [];
              session.subSessions?.forEach(ss => {
                const challengesActivity = ss.activities?.find(a => a.type === 'challenges');
                challengesActivity?.responses?.forEach(r => {
                  const resp = r.response || r;
                  if (Array.isArray(resp.challenges)) {
                    resp.challenges.forEach(c => {
                      if (c && c.trim()) allChallengeTexts.push(c.trim().toLowerCase());
                    });
                  } else if (resp.text) {
                    allChallengeTexts.push(resp.text.trim().toLowerCase());
                  } else if (resp.challenge) {
                    allChallengeTexts.push(resp.challenge.trim().toLowerCase());
                  }
                });
              });

              let topChallengeThemes = [];
              if (allChallengeTexts.length > 0) {
                const stopWords = new Set([
                  'i', 'me', 'my', 'we', 'our', 'you', 'your', 'he', 'she', 'it', 'they',
                  'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
                  'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been',
                  'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
                  'could', 'should', 'may', 'might', 'must', 'can', 'this', 'that',
                  'these', 'those', 'what', 'which', 'who', 'when', 'where', 'why', 'how',
                  'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some',
                  'such', 'no', 'not', 'only', 'same', 'so', 'than', 'too', 'very',
                  'just', 'also', 'now', 'here', 'there', 'then', 'if', 'because',
                  'as', 'until', 'while', 'about', 'into', 'through', 'during', 'before',
                  'after', 'above', 'below', 'up', 'down', 'out', 'off', 'over', 'under',
                  'again', 'am', 'get', 'got', 'like', 'need', 'want', 'know', 'think',
                  'make', 'find', 'feel', 'take', 'see', 'come', 'go', 'look', 'still'
                ]);

                const wordCount = {};
                const phraseCount = {};

                allChallengeTexts.forEach(text => {
                  const words = text
                    .replace(/[^\w\s]/g, ' ')
                    .split(/\s+/)
                    .filter(w => w.length > 2 && !stopWords.has(w));

                  words.forEach(word => {
                    wordCount[word] = (wordCount[word] || 0) + 1;
                  });

                  for (let i = 0; i < words.length - 1; i++) {
                    const phrase = `${words[i]} ${words[i + 1]}`;
                    phraseCount[phrase] = (phraseCount[phrase] || 0) + 1;
                  }
                });

                const combined = [];
                Object.entries(phraseCount)
                  .filter(([_, count]) => count >= 2)
                  .forEach(([phrase, count]) => {
                    combined.push({ word: phrase, count, isPhrase: true });
                  });
                Object.entries(wordCount)
                  .filter(([_, count]) => count >= 2)
                  .forEach(([word, count]) => {
                    const coveredByPhrase = combined.some(p =>
                      p.isPhrase && p.word.includes(word) && p.count >= count
                    );
                    if (!coveredByPhrase) {
                      combined.push({ word, count, isPhrase: false });
                    }
                  });

                topChallengeThemes = combined
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 8)
                  .map(item => ({
                    word: item.word,
                    count: item.count,
                    percentage: Math.round((item.count / allChallengeTexts.length) * 100)
                  }));
              }

              // Prefer the backend AI-grouped themes when available — they're
              // phrase-level (e.g. "Quick role transitions") instead of the
              // inline word-frequency single words. Normalize the AI shape to
              // match the inline shape so the renderer below doesn't care
              // about source.
              if (Array.isArray(aiThemes) && aiThemes.length > 0) {
                topChallengeThemes = aiThemes.map(t => ({
                  word: t.theme || t.word,
                  count: t.count || 0,
                  percentage: t.percentage || 0,
                }));
              }

              // Top Challenges — preferred source is the AI themes endpoint,
              // which semantically groups similar phrasing (e.g. "quick role
              // transition" + "fast switching between roles" become ONE
              // grouped challenge instead of two singletons). Without this,
              // exact-text matching gave every challenge a count of 1
              // because free-text responses almost never repeat verbatim.
              let topChallenges = [];
              if (Array.isArray(aiThemes) && aiThemes.length > 0) {
                topChallenges = aiThemes
                  .slice()
                  .sort((a, b) => (b.count || 0) - (a.count || 0))
                  .slice(0, 5)
                  .map(t => ({ text: t.theme || t.word, count: t.count || 0 }));
              } else {
                // Fallback — exact-text frequency. Filter to count >= 2 so we
                // don't pretend a "Top Challenges" ranking exists when every
                // submission is unique.
                const challengeFrequency = {};
                session.subSessions?.forEach(ss => {
                  const challengesActivity = ss.activities?.find(a => a.type === 'challenges');
                  challengesActivity?.responses?.forEach(r => {
                    const resp = r.response || r;
                    const texts = resp.challenges || (resp.challenge ? [resp.challenge] : (resp.text ? [resp.text] : []));
                    texts.forEach(text => {
                      if (text && text.trim()) {
                        const normalized = text.trim().toLowerCase();
                        if (!challengeFrequency[normalized]) {
                          challengeFrequency[normalized] = { original: text.trim(), count: 0 };
                        }
                        challengeFrequency[normalized].count++;
                      }
                    });
                  });
                });
                topChallenges = Object.values(challengeFrequency)
                  .filter(item => item.count >= 2)
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 5)
                  .map(item => ({ text: item.original, count: item.count }));
              }

              return (
                <>
                  {/* Challenge Density */}
                  {challengeDensity.length > 0 && (
                    <Card title="Challenge Density" padding="lg">
                      <p className="text-sm text-neutral-500 mb-4">
                        Sub-sessions with the most challenges reported
                      </p>
                      <div className="space-y-3">
                        {challengeDensity.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100"
                          >
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? 'bg-red-100 text-red-700' :
                              index === 1 ? 'bg-orange-100 text-orange-700' :
                              index === 2 ? 'bg-amber-100 text-amber-700' :
                              'bg-neutral-100 text-neutral-600'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-900">{item.subSessionName}</p>
                              <p className="text-xs text-neutral-500">{item.participantCount} participant{item.participantCount !== 1 ? 's' : ''} submitted</p>
                            </div>
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-700">
                                {item.challengeCount}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* Top Challenge Themes */}
                  {topChallengeThemes.length > 0 && (
                    <Card title="Top Challenge Themes" padding="lg">
                      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                        <p className="text-sm text-neutral-500">
                          {aiThemesSource === 'ai'
                            ? 'AI-grouped themes from challenge submissions'
                            : 'Most common words/phrases in challenge submissions'}
                        </p>
                        {aiThemesSource && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${
                            aiThemesSource === 'ai'
                              ? 'bg-purple-50 text-purple-700 ring-purple-200'
                              : 'bg-neutral-50 text-neutral-600 ring-neutral-200'
                          }`}>
                            {aiThemesSource === 'ai' ? '✨ AI-generated' : 'Word frequency'}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {topChallengeThemes.map((theme, index) => (
                          <div
                            key={index}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${
                              index === 0 ? 'bg-red-50 border-red-200 text-red-800' :
                              index === 1 ? 'bg-orange-50 border-orange-200 text-orange-800' :
                              index === 2 ? 'bg-amber-50 border-amber-200 text-amber-800' :
                              'bg-neutral-50 border-neutral-200 text-neutral-700'
                            }`}
                          >
                            <span className="font-medium capitalize">{theme.word}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              index === 0 ? 'bg-red-200 text-red-900' :
                              index === 1 ? 'bg-orange-200 text-orange-900' :
                              index === 2 ? 'bg-amber-200 text-amber-900' :
                              'bg-neutral-200 text-neutral-600'
                            }`}>
                              {theme.count}x ({theme.percentage}%)
                            </span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* Top Challenges Reported — sourced from the AI themes
                      endpoint when available, otherwise from exact-text
                      frequency (only when something repeats). */}
                  {topChallenges.length > 0 && (
                    <Card title="Top Challenges Reported" padding="lg">
                      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                        <p className="text-sm text-neutral-500">
                          {aiThemesSource === 'ai'
                            ? 'AI-grouped challenges across all participant submissions'
                            : 'Most frequently reported challenges by participants'}
                        </p>
                        {aiThemesSource && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${
                            aiThemesSource === 'ai'
                              ? 'bg-purple-50 text-purple-700 ring-purple-200'
                              : 'bg-neutral-50 text-neutral-600 ring-neutral-200'
                          }`}>
                            {aiThemesSource === 'ai' ? '✨ AI-generated' : 'Word frequency'}
                          </span>
                        )}
                      </div>
                      <div className="space-y-3">
                        {topChallenges.map((challenge, index) => (
                          <div
                            key={index}
                            className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100"
                          >
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? 'bg-amber-100 text-amber-700' :
                              index === 1 ? 'bg-neutral-200 text-neutral-700' :
                              index === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-neutral-100 text-neutral-600'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-neutral-800 break-words">{challenge.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Participants Tab */}
        {activeTab === 'participants' && (
          <div className="space-y-6">
            {sessionParticipants.length === 0 ? (
              <Card padding="lg">
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <p className="text-neutral-500">No participant data available</p>
                </div>
              </Card>
            ) : (
              <>
                {/* Participants Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-1.5 sm:gap-4">
                  <div className="bg-white rounded-xl border border-neutral-200 p-2.5 sm:p-4 flex items-center gap-3 sm:block">
                    <div className="flex items-center justify-between sm:mb-2 flex-1 sm:flex-none">
                      <span className="text-[11px] sm:text-xs text-neutral-500 font-medium">Total Joined</span>
                      <svg className="w-4 h-4 text-blue-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex items-center gap-1.5 sm:block">
                      <p className="text-sm sm:text-2xl font-bold text-neutral-900">{sessionParticipants.length}</p>
                      <p className="text-[10px] sm:text-xs text-neutral-500 sm:mt-1">participants</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-neutral-200 p-2.5 sm:p-4 flex items-center gap-3 sm:block">
                    <div className="flex items-center justify-between sm:mb-2 flex-1 sm:flex-none">
                      <span className="text-[11px] sm:text-xs text-neutral-500 font-medium">Avg Engagement</span>
                      <svg className="w-4 h-4 text-green-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <div className="flex items-center gap-1.5 sm:block">
                      <p className="text-sm sm:text-2xl font-bold text-green-600">{avgEngagement}%</p>
                      <p className="text-[10px] sm:text-xs text-neutral-500 sm:mt-1">response rate</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-neutral-200 p-2.5 sm:p-4 flex items-center gap-3 sm:block">
                    <div className="flex items-center justify-between sm:mb-2 flex-1 sm:flex-none">
                      <span className="text-[11px] sm:text-xs text-neutral-500 font-medium">Activities</span>
                      <svg className="w-4 h-4 text-purple-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div className="flex items-center gap-1.5 sm:block">
                      <p className="text-sm sm:text-2xl font-bold text-neutral-900">{launchedActivities}</p>
                      <p className="text-[10px] sm:text-xs text-neutral-500 sm:mt-1">launched</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-neutral-200 p-2.5 sm:p-4 flex items-center gap-3 sm:block">
                    <div className="flex items-center justify-between sm:mb-2 flex-1 sm:flex-none">
                      <span className="text-[11px] sm:text-xs text-neutral-500 font-medium">Total Responses</span>
                      <svg className="w-4 h-4 text-orange-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <div className="flex items-center gap-1.5 sm:block">
                      <p className="text-sm sm:text-2xl font-bold text-neutral-900">{totalResponses}</p>
                      <p className="text-[10px] sm:text-xs text-neutral-500 sm:mt-1">collected</p>
                    </div>
                  </div>
                </div>
                {/* Participants Table */}
                <Card
                  title={`All Participants (${sessionParticipants.length})`}
                  padding="lg"
                  actions={
                    sessionParticipants.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleDownloadAllReportsZip}
                        disabled={isGeneratingZip}
                        title="Download all participant reports (ZIP)"
                        aria-label="Download all participant reports as ZIP"
                      >
                        {isGeneratingZip ? (
                          <>
                            <svg className="w-4 h-4 sm:mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <span className="hidden sm:inline">Preparing…</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 sm:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span className="hidden sm:inline">All Reports (ZIP)</span>
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        title="Download participants list (PDF)"
                        aria-label="Download participants list PDF"
                        onClick={() => {
                          const doc = new jsPDF();

                          // Title
                          doc.setFontSize(18);
                          doc.setTextColor(37, 99, 235);
                          doc.text('Participants List', 14, 20);

                          // Session info
                          doc.setFontSize(10);
                          doc.setTextColor(100, 100, 100);
                          doc.text(`Session: ${session?.name || 'Untitled'}`, 14, 28);
                          doc.text(`Code: ${session?.code || 'N/A'}  |  Total: ${sessionParticipants.length} participants`, 14, 34);
                          doc.text(`Downloaded: ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`, 14, 40);

                          // Table
                          const tableData = sessionParticipants.map((p, i) => {
                            const name = p.name ||
                              (p.user?.firstName && p.user?.lastName
                                ? `${p.user.firstName} ${p.user.lastName}`
                                : p.user?.firstName || p.email || 'Anonymous');
                            const row = [
                              (i + 1).toString(),
                              name,
                              p.email || p.user?.email || '-',
                            ];
                            row.push(p.participantCode || '-');
                            if (showManagerCode) row.push(p.managerCode || '-');
                            row.push(p.participantOrganization || p.organization || '-');
                            row.push(p.status || 'joined');
                            return row;
                          });

                          const headRow = ['#', 'Name', 'Email', 'Participant Code'];
                          if (showManagerCode) headRow.push('Manager Code');
                          headRow.push('Organization', 'Status');

                          // Only the first columns have fixed widths; the rest size themselves, so the
                          // table stays correct however many optional columns are switched on.
                          const colStyles = { 0: { cellWidth: 10 }, 3: { cellWidth: 20, halign: 'center' } };

                          autoTable(doc, {
                            startY: 46,
                            head: [headRow],
                            body: tableData,
                            theme: 'striped',
                            styles: { fontSize: 9 },
                            headStyles: { fillColor: [37, 99, 235] },
                            columnStyles: colStyles,
                          });

                          doc.save(`participants_${session?.name || 'session'}_${new Date().toISOString().slice(0, 10)}.pdf`);
                        }}
                      >
                        <svg className="w-4 h-4 sm:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        <span className="hidden sm:inline">List</span>
                      </Button>
                    </div>
                    )
                  }
                >
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                      <thead>
                        <tr className="border-b border-neutral-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Participant</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Participant Code</th>
                          {showManagerCode && (
                            <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Manager Code</th>
                          )}
                          <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Organization</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Engagement</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Activities</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-neutral-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessionParticipants.map((participant, i) => {
                          const participantName = participant.name ||
                            (participant.user?.firstName && participant.user?.lastName
                              ? `${participant.user.firstName} ${participant.user.lastName}`
                              : participant.user?.firstName || participant.email || 'Anonymous');

                          const participantId = (participant._id || participant.id)?.toString();
                          const participantUserId = (participant.user?._id || participant.user)?.toString();
                          const participantEmails = [
                            participant.email,
                            participant.user?.email,
                          ].filter(Boolean).map(e => e?.toLowerCase());

                          let activitiesResponded = 0;

                          session.subSessions?.forEach(ss => {
                            ss.activities?.forEach(activity => {
                              if (activity.status !== 'launched' && activity.status !== 'completed' && activity.status !== 'active') {
                                return;
                              }

                              const participantResponse = activity.responses?.find(r => {
                                const responseParticipantId = (r.participant?._id || r.participant)?.toString();
                                if (participantId && responseParticipantId && participantId === responseParticipantId) return true;
                                if (participantUserId && responseParticipantId && participantUserId === responseParticipantId) return true;
                                const responseEmail = r.participantEmail || r.email;
                                if (responseEmail && participantEmails.length > 0 && participantEmails.includes(responseEmail.toLowerCase())) return true;
                                return false;
                              });

                              if (participantResponse) {
                                activitiesResponded++;
                              }
                            });
                          });

                          const engagementScore = launchedActivities > 0 ? Math.round((activitiesResponded / launchedActivities) * 100) : 0;

                          return (
                            <tr
                              key={participant._id || participant.id || i}
                              className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors"
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-sm font-bold text-primary-600">
                                      {participantName.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="font-medium text-neutral-900">{participantName}</p>
                                    {participant.email && (
                                      <p className="text-xs text-neutral-500">{participant.email}</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {participant.participantCode ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs font-mono font-medium">
                                    {participant.participantCode}
                                  </span>
                                ) : (
                                  <span className="text-neutral-400 text-sm">-</span>
                                )}
                              </td>
                              {showManagerCode && (
                                <td className="py-3 px-4 text-center">
                                  {participant.managerCode ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded bg-indigo-100 text-indigo-700 text-xs font-mono font-medium">
                                      {participant.managerCode}
                                    </span>
                                  ) : (
                                    <span className="text-neutral-400 text-sm">-</span>
                                  )}
                                </td>
                              )}
                              <td className="py-3 px-4">
                                <span className="text-sm text-neutral-600">
                                  {participant.organization || '-'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                                  engagementScore >= 70 ? 'bg-green-100 text-green-700' :
                                  engagementScore >= 40 ? 'bg-orange-100 text-orange-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {engagementScore}%
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="text-sm text-neutral-700">
                                  {activitiesResponded}/{launchedActivities}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge variant={participant.status === 'joined' ? 'success' : 'default'} size="sm">
                                  {participant.status || 'joined'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const basePath = isTrainerView ? '/trainer' : isAdminView ? '/admin' : '/client';
                                    navigate(`${basePath}/sessions/${sessionId}/participants/${participant._id || participant.id}`);
                                  }}
                                >
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card Layout */}
                  <div className="md:hidden space-y-2">
                    {sessionParticipants.map((participant, i) => {
                      const participantName = participant.name ||
                        (participant.user?.firstName && participant.user?.lastName
                          ? `${participant.user.firstName} ${participant.user.lastName}`
                          : participant.user?.firstName || participant.email || 'Anonymous');

                      const participantId = (participant._id || participant.id)?.toString();
                      const participantUserId = (participant.user?._id || participant.user)?.toString();
                      const participantEmails = [
                        participant.email,
                        participant.user?.email,
                      ].filter(Boolean).map(e => e?.toLowerCase());

                      let activitiesResponded = 0;

                      session.subSessions?.forEach(ss => {
                        ss.activities?.forEach(activity => {
                          if (activity.status !== 'launched' && activity.status !== 'completed' && activity.status !== 'active') return;
                          const participantResponse = activity.responses?.find(r => {
                            const responseParticipantId = (r.participant?._id || r.participant)?.toString();
                            if (participantId && responseParticipantId && participantId === responseParticipantId) return true;
                            if (participantUserId && responseParticipantId && participantUserId === responseParticipantId) return true;
                            const responseEmail = r.participantEmail || r.email;
                            if (responseEmail && participantEmails.length > 0 && participantEmails.includes(responseEmail.toLowerCase())) return true;
                            return false;
                          });
                          if (participantResponse) activitiesResponded++;
                        });
                      });

                      const engagementScore = launchedActivities > 0 ? Math.round((activitiesResponded / launchedActivities) * 100) : 0;

                      return (
                        <div
                          key={participant._id || participant.id || i}
                          className="border border-neutral-100 rounded-lg p-3 hover:bg-neutral-50 transition-colors"
                          onClick={() => {
                            const basePath = isTrainerView ? '/trainer' : isAdminView ? '/admin' : '/client';
                            navigate(`${basePath}/sessions/${sessionId}/participants/${participant._id || participant.id}`);
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-primary-600">
                                  {participantName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-neutral-900 truncate">{participantName}</p>
                                {participant.email && (
                                  <p className="text-[10px] text-neutral-500 truncate">{participant.email}</p>
                                )}
                              </div>
                            </div>
                            <Badge variant={participant.status === 'joined' ? 'success' : 'default'} size="sm">
                              {participant.status || 'joined'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                              engagementScore >= 70 ? 'bg-green-100 text-green-700' :
                              engagementScore >= 40 ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {engagementScore}%
                            </span>
                            <span className="text-neutral-500">{activitiesResponded}/{launchedActivities} activities</span>
                            {participant.participantCode && (
                              <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-mono font-medium">
                                {participant.participantCode}
                              </span>
                            )}
                            {showManagerCode && participant.managerCode && (
                              <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-mono font-medium">
                                {participant.managerCode}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="space-y-6">
            <Card title="Feedback Summary" padding="lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-neutral-700 mb-3 sm:mb-4">Overall Session Rating</h4>
                  <div className="text-center p-4 sm:p-6 bg-primary-50 rounded-lg">
                    <p className="text-3xl sm:text-5xl font-bold text-neutral-900">{overallSnapshot.avgRating}/5</p>
                    <p className="text-lg sm:text-xl mt-2">{'⭐'.repeat(Math.floor(overallSnapshot.avgRating))}</p>
                    <p className="text-xs sm:text-sm text-neutral-600 mt-2">{totalFeedbackResponses} responses</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm sm:text-base font-semibold text-neutral-700 mb-3 sm:mb-4">Rating Distribution</h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center justify-between p-2 sm:p-3 bg-red-100 rounded-lg">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-lg sm:text-2xl">😡</span>
                        <span className="text-xs sm:text-sm text-neutral-700">Very Disappointed (1)</span>
                      </div>
                      <span className="font-semibold text-neutral-900">
                        {emojiDistribution.veryDisappointed} ({totalFeedbackResponses > 0 ? Math.round((emojiDistribution.veryDisappointed / totalFeedbackResponses) * 100) : 0}%)
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 sm:p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-lg sm:text-2xl">😞</span>
                        <span className="text-xs sm:text-sm text-neutral-700">Disappointed (2)</span>
                      </div>
                      <span className="text-xs sm:text-base font-semibold text-neutral-900">
                        {emojiDistribution.disappointed} ({totalFeedbackResponses > 0 ? Math.round((emojiDistribution.disappointed / totalFeedbackResponses) * 100) : 0}%)
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 sm:p-3 bg-yellow-50 rounded-lg">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-lg sm:text-2xl">🙂</span>
                        <span className="text-xs sm:text-sm text-neutral-700">Neutral (3)</span>
                      </div>
                      <span className="text-xs sm:text-base font-semibold text-neutral-900">
                        {emojiDistribution.neutral} ({totalFeedbackResponses > 0 ? Math.round((emojiDistribution.neutral / totalFeedbackResponses) * 100) : 0}%)
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 sm:p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-lg sm:text-2xl">😄</span>
                        <span className="text-xs sm:text-sm text-neutral-700">Satisfied (4)</span>
                      </div>
                      <span className="text-xs sm:text-base font-semibold text-neutral-900">
                        {emojiDistribution.satisfied} ({totalFeedbackResponses > 0 ? Math.round((emojiDistribution.satisfied / totalFeedbackResponses) * 100) : 0}%)
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 sm:p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-lg sm:text-2xl">🤩</span>
                        <span className="text-xs sm:text-sm text-neutral-700">Excited (5)</span>
                      </div>
                      <span className="text-xs sm:text-base font-semibold text-neutral-900">
                        {emojiDistribution.excited} ({totalFeedbackResponses > 0 ? Math.round((emojiDistribution.excited / totalFeedbackResponses) * 100) : 0}%)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* 5-Question Feedback Breakdown */}
            {has5QuestionFeedback && (
              <Card title="Feedback by Question" padding="lg">
                <p className="text-xs sm:text-sm text-neutral-600 mb-3 sm:mb-4">Average ratings for each feedback question (1-5 scale)</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
                  {[
                    { key: 'topicRelevance', label: 'Topic Relevance', shortLabel: 'TRS', color: 'blue', getInterpretation: getTRSInterpretation },
                    { key: 'trainerEffectiveness', label: 'Trainer Effectiveness', shortLabel: 'TES', color: 'purple', getInterpretation: getTEIInterpretation },
                    { key: 'confidenceToApply', label: 'Confidence to Apply', shortLabel: 'PCI', color: 'green', getInterpretation: getPCSInterpretation },
                    { key: 'learningAdvocacy', label: 'Learning Advocacy', shortLabel: 'LAI', color: 'orange', getInterpretation: getLAIInterpretation },
                    { key: 'challengeResolution', label: 'Challenge Resolution', shortLabel: 'CRI', color: 'pink', getInterpretation: getCRIInterpretation },
                  ].map(({ key, label, shortLabel, color, getInterpretation }) => {
                    const data = feedbackQuestionAverages[key];
                    const colorClasses = {
                      blue: 'bg-blue-50 border-blue-200',
                      purple: 'bg-purple-50 border-purple-200',
                      green: 'bg-green-50 border-green-200',
                      orange: 'bg-orange-50 border-orange-200',
                      pink: 'bg-pink-50 border-pink-200',
                    };
                    const textClasses = {
                      blue: 'text-blue-700',
                      purple: 'text-purple-700',
                      green: 'text-green-700',
                      orange: 'text-orange-700',
                      pink: 'text-pink-700',
                    };
                    // Determine interpretation level based on view context
                    const interpretationLevel = isClientView ? 'client' : 'trainer';
                    const interpretation = data.avg > 0 ? getInterpretation(data.avg, interpretationLevel) : null;
                    return (
                      <div key={key} className={`p-3 sm:p-4 rounded-lg border ${colorClasses[color]} flex items-center gap-3 sm:block`}>
                        <div className="flex items-center justify-between sm:mb-2">
                          <span className={`text-xs sm:text-xs font-bold ${textClasses[color]}`}>{shortLabel}</span>
                          <span className="text-xs text-neutral-500 ml-2 sm:ml-0">{data.count}</span>
                        </div>
                        <p className={`text-lg sm:text-2xl font-bold ${textClasses[color]}`}>
                          {data.avg > 0 ? data.avg : '-'}<span className="text-xs sm:text-sm font-normal">/5</span>
                        </p>
                        <p className="text-xs sm:text-xs text-neutral-600 sm:mt-1">{label}</p>
                        {interpretation && (
                          <p className="text-[10px] sm:text-xs text-neutral-500 mt-1 sm:mt-2 leading-relaxed hidden sm:block">
                            {interpretation}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Pre & Post Session Tab */}
        {activeTab === 'pre-post' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-neutral-200 p-4">
                  <p className="text-xs text-neutral-500 font-medium">Total Participants</p>
                  <p className="text-2xl font-bold text-neutral-900 mt-2">{sessionParticipants.length}</p>
                  <p className="text-xs text-neutral-500 mt-1">joined the session</p>
                </div>
              {showPreSessionCol && (
                <div className="bg-white rounded-xl border border-neutral-200 p-4">
                  <p className="text-xs text-neutral-500 font-medium">Pre-Session Attended</p>
                  <p className="text-2xl font-bold text-blue-600 mt-2">{preSessionAttended}</p>
                  <p className="text-xs text-neutral-500 mt-1">{`of ${sessionParticipants.length} participants`}</p>
                </div>
              )}
              {showReflectionCol && (
                <div className="bg-white rounded-xl border border-neutral-200 p-4">
                  <p className="text-xs text-neutral-500 font-medium">Reflections Attended</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-2">{reflectionAttended}</p>
                  <p className="text-xs text-neutral-500 mt-1">{reflectionsOpen > 0 ? `of ${sessionParticipants.length} · ${reflectionsOpen} cycle${reflectionsOpen === 1 ? '' : 's'} open so far` : `of ${sessionParticipants.length} participants`}</p>
                </div>
              )}
            </div>

            <Card
              title={`Participation (${sessionParticipants.length})`}
              padding="lg"
              actions={
                sessionParticipants.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={exportPrePostCsv}>
                      <svg className="w-4 h-4 sm:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span className="hidden sm:inline">Excel</span>
                    </Button>
                    <Button variant="primary" size="sm" onClick={exportPrePostPdf}>
                      <svg className="w-4 h-4 sm:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span className="hidden sm:inline">PDF</span>
                    </Button>
                  </div>
                )
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Participant</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Participant Code</th>
                      {showPreSessionCol && (
                        <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Pre-Session</th>
                      )}
                      {showReflectionCol && (
                        <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Reflections</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sessionParticipants.map((participant, index) => (
                      <tr key={participant._id || index} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="py-3 px-4">
                          <p className="text-sm font-medium text-neutral-900">{participant.name}</p>
                          {participant.email && (
                            <p className="text-xs text-neutral-500">{participant.email}</p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {participant.participantCode ? (
                            <span className="inline-flex items-center px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs font-mono font-medium">
                              {participant.participantCode}
                            </span>
                          ) : (
                            <span className="text-neutral-400 text-sm">-</span>
                          )}
                        </td>
                        {showPreSessionCol && (
                          <td className="py-3 px-4 text-center">
                            {participant.preSession?.attended ? (
                              <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-medium">
                                {participant.preSession.totalQuestions > 0
                                  ? `Attended · ${participant.preSession.answered}/${participant.preSession.totalQuestions}`
                                  : 'Attended'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded bg-neutral-100 text-neutral-500 text-xs">Not attended</span>
                            )}
                          </td>
                        )}
                        {showReflectionCol && (
                          <td className="py-3 px-4 text-center">
                            {participant.reflections?.completed > 0 ? (
                              <span className="inline-flex items-center px-2 py-1 rounded bg-indigo-100 text-indigo-700 text-xs font-medium">
                                {`Attended · ${participant.reflections.completed}/${participant.reflections.open || participant.reflections.total}`}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded bg-neutral-100 text-neutral-500 text-xs">Not attended</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-neutral-400 mt-4">
                Pre-session counts anyone who completed the self-assessment, opened a material, or
                submitted a challenge or expectation. Reflections count cycles that have opened so
                far — later cycles are not counted as missed until they unlock.
              </p>
            </Card>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Performance Indices Card */}
            <Card className="bg-gradient-to-br from-primary-50 to-blue-50 border-primary-200">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Performance Indices</h3>
                  </div>
                  <span className="text-xs sm:text-sm text-neutral-500 hidden sm:inline">Session Analytics</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6">
                  {/* Topic Relevance Index */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                    className="flex flex-col items-center"
                  >
                    {performanceIndices.topicRelevanceIndex > 0 ? (
                      <>
                        <GaugeMeter
                          value={performanceIndices.topicRelevanceIndex}
                          maxValue={5}
                          label="Topic Relevance"
                          band={getMetricBandInfo('trs', performanceIndices.topicRelevanceIndex).label}
                          color={getMetricBandInfo('trs', performanceIndices.topicRelevanceIndex).color}
                          size="sm"
                        />
                        <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed hidden sm:block">
                          {getTRSInterpretation(performanceIndices.topicRelevanceIndex, isClientView ? 'client' : 'trainer')}
                        </p>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 sm:w-24 sm:h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-neutral-400 text-xs sm:text-sm">N/A</span>
                        </div>
                        <p className="text-xs font-medium text-neutral-700">Topic Relevance</p>
                        <span className="text-xs text-neutral-400">No data</span>
                      </div>
                    )}
                  </motion.div>

                  {/* Action Readiness Index */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    className="flex flex-col items-center"
                  >
                    {performanceIndices.actionReadinessIndex > 0 ? (
                      <>
                        <GaugeMeter
                          value={performanceIndices.actionReadinessIndex}
                          maxValue={1}
                          label="Action Readiness"
                          band={getMetricBandInfo('ars', performanceIndices.actionReadinessIndex).label}
                          color={getMetricBandInfo('ars', performanceIndices.actionReadinessIndex).color}
                          size="sm"
                        />
                        <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed hidden sm:block">
                          {getARSInterpretation(performanceIndices.actionReadinessIndex, isClientView ? 'client' : 'trainer')}
                        </p>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 sm:w-24 sm:h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-neutral-400 text-xs sm:text-sm">N/A</span>
                        </div>
                        <p className="text-xs font-medium text-neutral-700">Action Readiness</p>
                        <span className="text-xs text-neutral-400">No data</span>
                      </div>
                    )}
                  </motion.div>

                  {/* Learning Advocacy Index */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                    className="flex flex-col items-center"
                  >
                    {performanceIndices.learningAdvocacyIndex > 0 ? (
                      <>
                        <GaugeMeter
                          value={performanceIndices.learningAdvocacyIndex}
                          maxValue={5}
                          label="Learning Advocacy"
                          band={getMetricBandInfo('lai', performanceIndices.learningAdvocacyIndex).label}
                          color={getMetricBandInfo('lai', performanceIndices.learningAdvocacyIndex).color}
                          size="sm"
                        />
                        <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed hidden sm:block">
                          {getLAIInterpretation(performanceIndices.learningAdvocacyIndex, isClientView ? 'client' : 'trainer')}
                        </p>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 sm:w-24 sm:h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-neutral-400 text-xs sm:text-sm">N/A</span>
                        </div>
                        <p className="text-xs font-medium text-neutral-700">Learning Advocacy</p>
                        <span className="text-xs text-neutral-400">No data</span>
                      </div>
                    )}
                  </motion.div>

                  {/* Trainer Effectiveness Index */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 }}
                    className="flex flex-col items-center"
                  >
                    {performanceIndices.trainerEffectivenessIndex > 0 ? (
                      <>
                        <GaugeMeter
                          value={performanceIndices.trainerEffectivenessIndex}
                          maxValue={5}
                          label="Trainer Effectiveness"
                          band={getMetricBandInfo('tei', performanceIndices.trainerEffectivenessIndex).label}
                          color={getMetricBandInfo('tei', performanceIndices.trainerEffectivenessIndex).color}
                          size="sm"
                        />
                        <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed hidden sm:block">
                          {getTEIInterpretation(performanceIndices.trainerEffectivenessIndex, isClientView ? 'client' : 'trainer')}
                        </p>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 sm:w-24 sm:h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-neutral-400 text-xs sm:text-sm">N/A</span>
                        </div>
                        <p className="text-xs font-medium text-neutral-700">Trainer Effectiveness</p>
                        <span className="text-xs text-neutral-400">No data</span>
                      </div>
                    )}
                  </motion.div>

                  {/* Challenge Resolution Index */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="flex flex-col items-center"
                  >
                    {performanceIndices.challengeResolutionIndex > 0 ? (
                      <>
                        <GaugeMeter
                          value={performanceIndices.challengeResolutionIndex}
                          maxValue={5}
                          label="Challenge Resolution"
                          band={getMetricBandInfo('cri', performanceIndices.challengeResolutionIndex).label}
                          color={getMetricBandInfo('cri', performanceIndices.challengeResolutionIndex).color}
                          size="sm"
                        />
                        <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed hidden sm:block">
                          {getCRIInterpretation(performanceIndices.challengeResolutionIndex, isClientView ? 'client' : 'trainer')}
                        </p>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="w-16 h-16 sm:w-24 sm:h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-neutral-400 text-xs sm:text-sm">N/A</span>
                        </div>
                        <p className="text-xs font-medium text-neutral-700">Challenge Resolution</p>
                        <span className="text-xs text-neutral-400">No data</span>
                      </div>
                    )}
                  </motion.div>
                </div>
              </div>
            </Card>

            {/* Activity Totals Card */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <Card className="bg-gradient-to-br from-rose-50 to-red-50 border-rose-200">
                <div className="p-4 sm:p-6 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <p className="text-xs sm:text-sm text-rose-700 font-medium mb-1">Total Challenges</p>
                  <p className="text-2xl sm:text-3xl font-bold text-rose-600">{overallSnapshot.totalChallenges}</p>
                  <p className="text-[10px] sm:text-xs text-neutral-500 mt-1">Reported by participants</p>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
                <div className="p-4 sm:p-6 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                  <p className="text-xs sm:text-sm text-purple-700 font-medium mb-1">Total Action Plans</p>
                  <p className="text-2xl sm:text-3xl font-bold text-purple-600">{overallSnapshot.totalActionPlans}</p>
                  <p className="text-[10px] sm:text-xs text-neutral-500 mt-1">Created by participants</p>
                </div>
              </Card>
            </div>

            {/* No Data Message */}
            {!has5QuestionFeedback && performanceIndices.actionReadinessIndex === 0 && (
              <Card padding="lg">
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto text-neutral-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-neutral-700 mb-2">No Analytics Data Yet</h3>
                  <p className="text-neutral-500 max-w-md mx-auto">
                    Performance indices will be available once participants complete feedback activities and action plans during the session.
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}

      </div>

        {/* AI Impact Report Tab */}
        {activeTab === 'ai-report' && (
          <div className="space-y-6">
            {/* Generate + History Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-neutral-900">AI Impact Reports</h2>
                <p className="text-sm text-neutral-500">AI-generated analysis of session data</p>
              </div>
              <div className="flex gap-2">
                {aiReport && !aiReport.error && (
                  <button
                    onClick={handleDownloadAiSessionReport}
                    disabled={isAiPdfGenerating}
                    className="px-5 py-2.5 bg-neutral-900 text-white rounded-xl text-sm font-bold hover:bg-neutral-800 transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    {isAiPdfGenerating ? 'Generating...' : 'Download Report'}
                  </button>
                )}
                <button
                  onClick={async () => {
                    setAiReportLoading(true);
                    setAiReport(null);
                    try {
                      const res = await api.post(`/ai/reports/session/${sessionId}`, {});
                      if (res.success && !res.data?.error) {
                        setAiReport(res.data);
                        try {
                          const histRes = await api.get(`/ai/reports/session/${sessionId}/history`);
                          if (histRes.success) setAiReportHistory(histRes.data);
                        } catch {}
                      } else {
                        // Prefer the backend's friendly userMessage (rate-limit /
                        // high-demand) over the raw error code/stack trace.
                        setAiReport({
                          error: res.data?.error || 'AI not available',
                          userMessage: res.data?.userMessage,
                        });
                      }
                    } catch (err) {
                      setAiReport({ error: err.message });
                    } finally {
                      setAiReportLoading(false);
                    }
                  }}
                  disabled={aiReportLoading}
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-bold hover:bg-primary-600 transition-all shadow-lg shadow-primary-200 disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                  {aiReportLoading ? 'Generating...' : 'Generate New Report'}
                </button>
              </div>
            </div>

            {/* Report History */}
            {aiReportHistory.length > 0 && !aiReport && !aiReportLoading && (
              <Card padding="md">
                <h3 className="text-sm font-semibold text-neutral-700 mb-3">Previous Reports</h3>
                <div className="space-y-2">
                  {aiReportHistory.map((report) => (
                    <button
                      key={report._id}
                      onClick={async () => {
                        setAiReportLoading(true);
                        try {
                          const res = await api.get(`/ai/reports/${report._id}`);
                          if (res.success) setAiReport(res.data);
                        } catch (err) {
                          console.error('Error loading report:', err);
                        } finally {
                          setAiReportLoading(false);
                        }
                      }}
                      className="w-full flex items-start gap-3 p-3 bg-neutral-50 hover:bg-neutral-100 rounded-xl border border-neutral-200 transition-colors text-left"
                    >
                      <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-neutral-900">
                            {new Date(report.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {' at '}
                            {new Date(report.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <svg className="w-4 h-4 text-neutral-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        </div>
                        <p className="text-xs text-neutral-500 mt-0.5">By {report.generatedBy}</p>
                        {report.summary && <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{report.summary}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* No reports yet and not loading */}
            {!aiReport && !aiReportLoading && aiReportHistory.length === 0 && (
              <Card padding="lg">
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-2">No Reports Yet</h3>
                  <p className="text-sm text-neutral-500 max-w-md mx-auto">
                    Click "Generate New Report" to create an AI-powered analysis of your session data. All generated reports are saved and can be viewed anytime.
                  </p>
                </div>
              </Card>
            )}

            {aiReportLoading && (
              <Card padding="lg">
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-sm text-primary-600 font-medium">AI is analyzing session data...</p>
                  <p className="text-xs text-neutral-400 mt-1">This may take 10-20 seconds</p>
                </div>
              </Card>
            )}

            {aiReport?.error && (
              <Card padding="lg">
                <div className="text-center py-8">
                  <p className="text-lg font-bold text-neutral-900 mb-2">AI Not Available</p>
                  <p className="text-sm text-neutral-500 mb-4">{aiReport.userMessage || (typeof aiReport.error === 'string' && !['rate_limited', 'overloaded'].includes(aiReport.error) ? aiReport.error : 'Aktivity Intelligence™ (AI) is temporarily unavailable. Please try again in a few minutes.')}</p>
                  <button onClick={() => setAiReport(null)} className="px-6 py-2 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-200">Dismiss</button>
                </div>
              </Card>
            )}

            {aiReport && !aiReport.error && (
              <div className="space-y-4">
                {/* Report timestamp */}
                {aiReport.createdAt && (
                  <div className="flex items-center gap-2 text-xs text-neutral-500">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Generated on {new Date(aiReport.createdAt).toLocaleString('en-IN')}
                    {aiReport.generatedBy && ` by ${aiReport.generatedBy}`}
                    <button
                      onClick={() => setAiReport(null)}
                      className="ml-auto text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Back to History
                    </button>
                  </div>
                )}
                {/* Executive Summary */}
                {aiReport.executiveSummary && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Executive Summary</h3>
                    <p className="text-sm text-neutral-700 leading-relaxed">{aiReport.executiveSummary}</p>
                  </Card>
                )}

                {/* Pre-Session Readiness (for scheduled sessions) */}
                {aiReport.preSessionReadiness && (
                  <Card padding="lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-neutral-900">Pre-Session Readiness</h3>
                      <Badge variant={aiReport.preSessionReadiness.overallScore === 'excellent' ? 'success' : aiReport.preSessionReadiness.overallScore === 'high' ? 'success' : aiReport.preSessionReadiness.overallScore === 'moderate' ? 'warning' : 'secondary'}>
                        {aiReport.preSessionReadiness.overallScore}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {aiReport.preSessionReadiness.materialCompletion && (
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600 font-medium mb-1">Material Completion</p>
                          <p className="text-sm text-blue-900">{aiReport.preSessionReadiness.materialCompletion}</p>
                        </div>
                      )}
                      {aiReport.preSessionReadiness.selfAssessmentInsights && (
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-green-600 font-medium mb-1">Self-Assessment Insights</p>
                          <p className="text-sm text-green-900">{aiReport.preSessionReadiness.selfAssessmentInsights}</p>
                        </div>
                      )}
                      {aiReport.preSessionReadiness.challengeAnalysis && (
                        <div className="p-3 bg-amber-50 rounded-lg">
                          <p className="text-xs text-amber-600 font-medium mb-1">Participant Workplace Challenges</p>
                          <p className="text-sm text-amber-900">{aiReport.preSessionReadiness.challengeAnalysis}</p>
                        </div>
                      )}
                      {aiReport.preSessionReadiness.expectationAlignment && (
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-purple-600 font-medium mb-1">Expectation Alignment</p>
                          <p className="text-sm text-purple-900">{aiReport.preSessionReadiness.expectationAlignment}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Trainer Preparation (for scheduled sessions) */}
                {aiReport.trainerPreparation && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Trainer Preparation Guide</h3>
                    <p className="text-sm text-neutral-700 mb-4">{aiReport.trainerPreparation.summary}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {aiReport.trainerPreparation.topChallengeThemes?.length > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                          <p className="text-xs text-amber-700 font-bold mb-2">Workplace Challenge Themes to Address</p>
                          {aiReport.trainerPreparation.topChallengeThemes.map((t, i) => (
                            <p key={i} className="text-sm text-amber-800 mb-1">- {t}</p>
                          ))}
                        </div>
                      )}
                      {aiReport.trainerPreparation.suggestedFocusAreas?.length > 0 && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs text-blue-700 font-bold mb-2">Suggested Focus Areas</p>
                          {aiReport.trainerPreparation.suggestedFocusAreas.map((a, i) => (
                            <p key={i} className="text-sm text-blue-800 mb-1">- {a}</p>
                          ))}
                        </div>
                      )}
                    </div>
                    {aiReport.trainerPreparation.customizationTips?.length > 0 && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-100">
                        <p className="text-xs text-green-700 font-bold mb-2">Customization Tips</p>
                        {aiReport.trainerPreparation.customizationTips.map((t, i) => (
                          <p key={i} className="text-sm text-green-800 mb-1">- {t}</p>
                        ))}
                      </div>
                    )}
                  </Card>
                )}

                {/* Training Effectiveness */}
                {aiReport.trainingEffectiveness && (
                  <Card padding="lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-neutral-900">Training Effectiveness</h3>
                      <Badge variant={aiReport.trainingEffectiveness.overallScore === 'excellent' ? 'success' : aiReport.trainingEffectiveness.overallScore === 'high' ? 'success' : aiReport.trainingEffectiveness.overallScore === 'moderate' ? 'warning' : 'secondary'}>
                        {aiReport.trainingEffectiveness.overallScore}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      <div className="p-3 bg-neutral-50 rounded-lg">
                        <p className="text-xs text-neutral-500 font-medium mb-1">Evidence</p>
                        <p className="text-sm text-neutral-700">{aiReport.trainingEffectiveness.evidence}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-600 font-medium mb-1">Pre-Session</p>
                          <p className="text-sm text-blue-900">{aiReport.trainingEffectiveness.preSessionEngagement}</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-green-600 font-medium mb-1">In-Session</p>
                          <p className="text-sm text-green-900">{aiReport.trainingEffectiveness.inSessionEngagement}</p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-purple-600 font-medium mb-1">Post-Session</p>
                          <p className="text-sm text-purple-900">{aiReport.trainingEffectiveness.postSessionApplication}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Challenge Resolution */}
                {aiReport.challengeResolution && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Challenge Resolution</h3>
                    <p className="text-sm text-neutral-700 mb-3">{aiReport.challengeResolution.summary}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {aiReport.challengeResolution.topChallengesAddressed?.length > 0 && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-xs text-green-600 font-bold mb-2">Addressed</p>
                          {aiReport.challengeResolution.topChallengesAddressed.map((c, i) => (
                            <p key={i} className="text-sm text-green-800 mb-1">- {c}</p>
                          ))}
                        </div>
                      )}
                      {aiReport.challengeResolution.unaddressedGaps?.length > 0 && (
                        <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                          <p className="text-xs text-red-600 font-bold mb-2">Gaps Remaining</p>
                          {aiReport.challengeResolution.unaddressedGaps.map((c, i) => (
                            <p key={i} className="text-sm text-red-800 mb-1">- {c}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Action Plan Analysis */}
                {aiReport.actionPlanAnalysis && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Action Plan Analysis</h3>
                    <div className="space-y-3">
                      {['qualityAssessment', 'completionTrend', 'blockerPatterns', 'managerEngagement'].map(key => (
                        aiReport.actionPlanAnalysis[key] && (
                          <div key={key} className="p-3 bg-neutral-50 rounded-lg">
                            <p className="text-xs text-neutral-500 font-medium mb-1">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</p>
                            <p className="text-sm text-neutral-700">{aiReport.actionPlanAnalysis[key]}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </Card>
                )}

                {/* Key Metrics */}
                {aiReport.keyMetrics && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {aiReport.keyMetrics.learningTransferRate && (
                      <Card padding="md">
                        <p className="text-xs text-neutral-500 font-medium mb-1">Learning Transfer Rate</p>
                        <p className="text-lg font-bold text-primary-600">{aiReport.keyMetrics.learningTransferRate}</p>
                      </Card>
                    )}
                    {aiReport.keyMetrics.managerSupportRate && (
                      <Card padding="md">
                        <p className="text-xs text-neutral-500 font-medium mb-1">Manager Support Rate</p>
                        <p className="text-lg font-bold text-green-600">{aiReport.keyMetrics.managerSupportRate}</p>
                      </Card>
                    )}
                    {aiReport.keyMetrics.sustainedEngagement && (
                      <Card padding="md">
                        <p className="text-xs text-neutral-500 font-medium mb-1">Sustained Engagement</p>
                        <p className="text-lg font-bold text-blue-600">{aiReport.keyMetrics.sustainedEngagement}</p>
                      </Card>
                    )}
                  </div>
                )}

                {/* Strengths & Improvements */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aiReport.strengths?.length > 0 && (
                    <Card padding="lg">
                      <h3 className="text-base font-bold text-green-800 mb-3">Strengths</h3>
                      <div className="space-y-2">
                        {aiReport.strengths.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-green-50 rounded-lg">
                            <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                            <p className="text-sm text-green-800">{s}</p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                  {aiReport.improvements?.length > 0 && (
                    <Card padding="lg">
                      <h3 className="text-base font-bold text-amber-800 mb-3">Areas for Improvement</h3>
                      <div className="space-y-2">
                        {aiReport.improvements.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg">
                            <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" /></svg>
                            <p className="text-sm text-amber-800">{s}</p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>

                {/* Key Insights (flat list returned by some models) */}
                {Array.isArray(aiReport.insights) && aiReport.insights.length > 0 && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Key Insights</h3>
                    <div className="space-y-2">
                      {aiReport.insights.map((s, i) => {
                        const text = typeof s === 'string' ? s : (s.text || s.insight || '');
                        if (!text) return null;
                        return (
                          <div key={i} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                            <span className="w-6 h-6 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                            <p className="text-sm text-neutral-700">{text}</p>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* Recommendations */}
                {aiReport.recommendations?.length > 0 && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Recommendations</h3>
                    <div className="space-y-3">
                      {aiReport.recommendations.map((r, i) => {
                        // The AI may return recommendations as plain strings or as
                        // { recommendation, priority, expectedImpact } objects.
                        const isStr = typeof r === 'string';
                        const text = isStr ? r : (r.recommendation || r.title || r.text || '');
                        if (!text) return null;
                        const priority = isStr ? '' : (r.priority || '');
                        const expectedImpact = isStr ? '' : r.expectedImpact;
                        return (
                          <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-neutral-200">
                            {priority ? (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase flex-shrink-0 ${
                                priority === 'high' ? 'bg-red-100 text-red-700' :
                                priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>{priority}</span>
                            ) : (
                              <span className="w-6 h-6 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                            )}
                            <div>
                              <p className="text-sm font-semibold text-neutral-900">{text}</p>
                              {expectedImpact ? <p className="text-xs text-neutral-500 mt-0.5">{expectedImpact}</p> : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* Next Steps */}
                {aiReport.nextSteps?.length > 0 && (
                  <Card padding="lg">
                    <h3 className="text-lg font-bold text-neutral-900 mb-3">Immediate Next Steps</h3>
                    <div className="space-y-2">
                      {aiReport.nextSteps.map((step, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-primary-50 rounded-lg border border-primary-100">
                          <span className="w-6 h-6 rounded-full bg-primary-200 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                          <p className="text-sm text-primary-900">{step}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

              </div>
            )}
          </div>
        )}

      {/* QR Code Modal */}
      <Modal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        title="Session QR Code"
        size="md"
      >
        <div className="p-4">
          <QRCodeGenerator
            sessionCode={session.code}
            sessionName={session.name}
          />
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default SessionReportPage;
